/**
 * MEVIA v5.0 - NOM-035 Compliance Panel
 * 
 * Implementación completa según documentos:
 * - M8-A: Configuración IPA (9 pasos)
 * - M8-B: Guías I y III
 * - M8-C: Dashboard IPA (4 bloques)
 */

import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { Slider } from './ui/slider';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  Users,
  Calendar,
  ChevronRight,
  RefreshCw,
  Building2,
  User,
  ClipboardList,
  BarChart3,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  Check,
  X,
  Upload,
  Megaphone,
  FileCheck,
  MessageSquare,
  PartyPopper,
  Download,
  Settings,
  Eye,
  Zap,
  FolderOpen,
  Activity,
  Target,
  TrendingUp,
  PieChart,
  Mail,
  Inbox,
  Send,
  Search,
  Copy,
  Filter,
  ChevronsUpDown,
  ExternalLink,
  LayoutGrid,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import NOM035DashboardV2 from './NOM035DashboardV2';
import ATSReferralDialog from './ATSReferralDialog';
import ProgramaAtencionM8M from './ProgramaAtencionM8M';
import CanalizacionATSM8L from './CanalizacionATSM8L';
import logger from '../utils/logger';

// ============== CONSTANTS ==============

// M8-F: Textos predefinidos para la Política de Prevención NOM-035
const POLICY_TEMPLATES = {
  declaracion_principios: (nombreEmpresa, representanteLegal) => 
`${nombreEmpresa}, representada por ${representanteLegal}, declara su compromiso irrevocable con la protección de la salud psicológica y el bienestar integral de sus trabajadores, en cumplimiento de la NOM-035-STPS-2018.

Esta empresa reconoce que los factores de riesgo psicosocial en el trabajo pueden afectar negativamente la salud física y mental de las personas, así como la productividad organizacional. En consecuencia, asumimos la identificación, análisis y prevención de dichos factores como una obligación legal y una responsabilidad ética con nuestros colaboradores.

Esta política aplica a todos los trabajadores de ${nombreEmpresa}, sin distinción de nivel jerárquico, tipo de contrato o modalidad de trabajo. Su vigencia es permanente y será revisada anualmente o ante cambios significativos en la organización.`,

  compromisos_especificos: (nombreEmpresa, responsableNom, cargoResponsable) =>
`${nombreEmpresa} se compromete a:

1. Identificar y evaluar los factores de riesgo psicosocial presentes en el centro de trabajo de forma periódica, aplicando los instrumentos establecidos en la NOM-035-STPS-2018 al menos cada dos años.

2. Implementar medidas de prevención y control para los factores de riesgo identificados, con planes de acción documentados, responsables asignados y plazos definidos.

3. Promover activamente un entorno organizacional favorable, que incluya: claridad en las funciones y responsabilidades de cada puesto; canales de comunicación efectivos entre colaboradores y directivos; reconocimiento del desempeño; y condiciones que permitan equilibrar la vida laboral y personal.

4. Prevenir, atender y sancionar los actos de violencia laboral —incluyendo hostigamiento y acoso— mediante procedimientos claros, confidenciales y libres de represalias.

5. Identificar a trabajadores expuestos a acontecimientos traumáticos severos durante o con motivo del trabajo, y canalizarlos oportunamente para su atención médica y psicológica.

6. Capacitar a directivos, gerentes y supervisores en la prevención de factores de riesgo psicosocial y en la promoción de un liderazgo positivo.

7. Comunicar a todos los colaboradores los resultados de las evaluaciones y las acciones implementadas, garantizando la confidencialidad de los datos individuales.

El Responsable de la implementación y seguimiento de esta política es ${responsableNom}${cargoResponsable ? `, ${cargoResponsable}` : ''}.`,

  mecanismo_denuncia: (nombreEmpresa, responsableNom, cargoResponsable) =>
`${nombreEmpresa} ha establecido los siguientes mecanismos seguros y confidenciales para que los trabajadores puedan reportar prácticas contrarias al entorno organizacional favorable y actos de violencia laboral:

Buzón de Comunicación NOM-035: Canal digital integrado en la plataforma MEVIA, accesible para todos los colaboradores desde su perfil de usuario. Permite enviar reportes de forma confidencial. El único destinatario es ${responsableNom}${cargoResponsable ? `, ${cargoResponsable}` : ''}, quien tiene obligación legal de reserva sobre la identidad del remitente conforme a la NOM-035-STPS-2018. Para reportes que no impliquen canalización obligatoria, el colaborador puede optar por el envío anónimo.

Contacto directo con el Responsable NOM-035: Los trabajadores también pueden dirigirse directamente a ${responsableNom} de forma presencial o por los medios de comunicación internos de la empresa.

Esta empresa garantiza expresamente que no habrá represalias de ningún tipo contra quienes utilicen estos canales de denuncia. Cualquier acto de represalia será considerado una falta grave y estará sujeto a las medidas disciplinarias y consecuencias legales correspondientes.`
};

const IPA_COLORS = {
  VERDE: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-500', progress: 'bg-green-500', hex: '#27AE60' },
  AMARILLO: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-500', progress: 'bg-yellow-500', hex: '#F39C12' },
  NARANJA: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-500', progress: 'bg-orange-500', hex: '#E67E22' },
  ROJO: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-500', progress: 'bg-red-500', hex: '#C0392B' }
};

const RISK_COLORS = {
  NULO: { bg: 'bg-green-200', text: 'text-green-900', hex: '#27AE60' },
  BAJO: { bg: 'bg-blue-200', text: 'text-blue-900', hex: '#3498DB' },
  MEDIO: { bg: 'bg-yellow-200', text: 'text-yellow-900', hex: '#F39C12' },
  ALTO: { bg: 'bg-orange-200', text: 'text-orange-900', hex: '#E67E22' },
  MUY_ALTO: { bg: 'bg-red-200', text: 'text-red-900', hex: '#C0392B' }
};

const STEP_ORDER = ['DATOS_EMPRESA', 'RESPONSABLE', 'POLITICA', 'DIFUSION', 'GUIA_I', 'GUIA_II_III', 'PROGRAMA', 'EVIDENCIAS', 'COMUNICACION'];

const STEP_ICONS = {
  DATOS_EMPRESA: Building2,
  RESPONSABLE: User,
  POLITICA: FileText,
  DIFUSION: Megaphone,
  GUIA_I: AlertCircle,
  GUIA_II_III: ClipboardList,
  PROGRAMA: FileCheck,
  EVIDENCIAS: Upload,
  COMUNICACION: MessageSquare
};

// Expediente sections (10 items per M8-C)
const EXPEDIENTE_SECTIONS = [
  { key: 'politica', name_es: 'Política de prevención', name_en: 'Prevention policy', step: 'POLITICA' },
  { key: 'difusion', name_es: 'Evidencia de difusión', name_en: 'Diffusion evidence', step: 'DIFUSION' },
  { key: 'guia_i', name_es: 'Resultados Guía I', name_en: 'Guide I results', step: 'GUIA_I' },
  { key: 'guia_ii_iii', name_es: 'Resultados Guía II/III', name_en: 'Guide II/III results', step: 'GUIA_II_III' },
  { key: 'programa', name_es: 'Programa de atención', name_en: 'Attention program', step: 'PROGRAMA' },
  { key: 'acciones', name_es: 'Acciones implementadas', name_en: 'Implemented actions', step: 'PROGRAMA' },
  { key: 'evidencias', name_es: 'Evidencias documentales', name_en: 'Documentary evidence', step: 'EVIDENCIAS' },
  { key: 'comunicacion', name_es: 'Comunicación a trabajadores', name_en: 'Worker communication', step: 'COMUNICACION' },
  { key: 'canalizaciones', name_es: 'Constancias de canalización', name_en: 'Referral certificates', step: 'GUIA_I' },
  { key: 'responsable', name_es: 'Designación del responsable', name_en: 'Responsible designation', step: 'RESPONSABLE' }
];

const getIPAStatus = (score) => {
  if (score >= 90) return 'VERDE';
  if (score >= 70) return 'AMARILLO';
  if (score >= 50) return 'NARANJA';
  return 'ROJO';
};

const TEXTS = {
  es: {
    title: 'NOM-035 Compliance',
    subtitle: 'Cumplimiento de la Norma Oficial Mexicana de Factores de Riesgo Psicosocial',
    ipaMessages: {
      VERDE: 'Tu empresa está lista para una inspección sorpresa de la STPS',
      AMARILLO: 'Casi listo — completa estos pasos para estar protegido',
      NARANJA: 'Riesgo moderado ante inspección. Hay pasos críticos pendientes',
      ROJO: 'Riesgo alto. Una inspección hoy podría resultar en multa de hasta $542,850 MXN'
    },
    steps: {
      DATOS_EMPRESA: 'Datos de la empresa', RESPONSABLE: 'Responsable NOM-035', POLITICA: 'Política de prevención',
      DIFUSION: 'Difusión de política', GUIA_I: 'Guía I - ATS', GUIA_II_III: 'Guía II/III - Riesgos',
      PROGRAMA: 'Programa de atención', EVIDENCIAS: 'Evidencias', COMUNICACION: 'Comunicación de resultados'
    },
    blocks: {
      ipa: 'Índice de Preparación para Auditoría',
      riskMap: 'Índice NOM-035 / Mapa de Calor',
      program: 'Programa de Atención',
      expediente: 'Expediente de Cumplimiento'
    },
    actions: {
      viewDetail: 'Ver detalle',
      exportExpediente: 'Exportar expediente',
      goToConfig: 'Ir a configuración',
      applyGuide: 'Aplicar Guía',
      sendReminder: 'Enviar recordatorio masivo'
    },
    riskLevels: { NULO: 'Nulo', BAJO: 'Bajo', MEDIO: 'Medio', ALTO: 'Alto', MUY_ALTO: 'Muy Alto' },
    nextActions: 'Próximas Acciones',
    stepsCompleted: 'pasos completados',
    daysUntilReeval: 'días para reevaluación',
    noGuideApplied: 'Aplica la Guía III para ver el diagnóstico de riesgo psicosocial',
    expedienteReady: 'Expediente listo para auditoría',
    expedienteIncomplete: 'secciones pendientes',
    alertTypes: { URGENTE: 'URGENTE', ALTA: 'ALTA', MEDIA: 'MEDIA' },
    guide1Title: 'Identificación de Acontecimiento Traumático Severo (ATS)',
    guide3Title: 'Identificación de Factores de Riesgo Psicosocial',
    confidential: 'Tu información es confidencial',
    submitAnswers: 'Enviar respuestas',
    startConfig: 'Iniciar Configuración',
    continueConfig: 'Continuar Configuración'
  },
  en: {
    title: 'NOM-035 Compliance',
    subtitle: 'Mexican Official Standard for Psychosocial Risk Factors Compliance',
    ipaMessages: {
      VERDE: 'Your company is ready for a surprise STPS inspection',
      AMARILLO: 'Almost ready — complete these steps to be protected',
      NARANJA: 'Moderate inspection risk. Critical steps pending',
      ROJO: 'High risk. An inspection today could result in a fine of up to $542,850 MXN'
    },
    steps: {
      DATOS_EMPRESA: 'Company data', RESPONSABLE: 'NOM-035 Responsible', POLITICA: 'Prevention policy',
      DIFUSION: 'Policy diffusion', GUIA_I: 'Guide I - STE', GUIA_II_III: 'Guide II/III - Risks',
      PROGRAMA: 'Attention program', EVIDENCIAS: 'Evidence', COMUNICACION: 'Results communication'
    },
    blocks: {
      ipa: 'Audit Preparation Index',
      riskMap: 'NOM-035 Index / Heat Map',
      program: 'Attention Program',
      expediente: 'Compliance File'
    },
    actions: {
      viewDetail: 'View detail',
      exportExpediente: 'Export file',
      goToConfig: 'Go to configuration',
      applyGuide: 'Apply Guide',
      sendReminder: 'Send mass reminder'
    },
    riskLevels: { NULO: 'Null', BAJO: 'Low', MEDIO: 'Medium', ALTO: 'High', MUY_ALTO: 'Very High' },
    nextActions: 'Next Actions',
    stepsCompleted: 'steps completed',
    daysUntilReeval: 'days until reevaluation',
    noGuideApplied: 'Apply Guide III to see the psychosocial risk diagnosis',
    expedienteReady: 'File ready for audit',
    expedienteIncomplete: 'pending sections',
    alertTypes: { URGENTE: 'URGENT', ALTA: 'HIGH', MEDIA: 'MEDIUM' },
    guide1Title: 'Identification of Severe Traumatic Event (STE)',
    guide3Title: 'Identification of Psychosocial Risk Factors',
    confidential: 'Your information is confidential',
    submitAnswers: 'Submit answers',
    startConfig: 'Start Configuration',
    continueConfig: 'Continue Configuration'
  }
};

// ============== GUÍA II/III PARTICIPATION COMPONENT ==============
// M8-G: Componente para validar participación mínima según NOM-035

const GuiaIIIIIStep = ({ config, formData, setFormData, empresaId }) => {
  const { language } = useLanguage();
  const [participationData, setParticipationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showException, setShowException] = useState(false);

  // Fórmula oficial para calcular tamaño de muestra (empresas >50 trabajadores)
  const calculateMinSampleSize = (N) => {
    if (N <= 50) return N; // 100% para empresas de 16-50
    // n = 0.9604 × N / (0.0025 × (N − 1) + 0.9604)
    return Math.ceil((0.9604 * N) / (0.0025 * (N - 1) + 0.9604));
  };

  useEffect(() => {
    loadParticipationData();
  }, [empresaId]);

  const loadParticipationData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/v5/nom035/${empresaId}/participation-status`);
      if (response.data.success) {
        setParticipationData(response.data.data);
      }
    } catch (error) {
      logger.error('Error loading participation:', error);
      // Fallback con datos del config
      const totalWorkers = config?.numero_trabajadores || 50;
      const minRequired = calculateMinSampleSize(totalWorkers);
      setParticipationData({
        total_trabajadores: totalWorkers,
        cuestionarios_completados: 0,
        muestra_minima: minRequired,
        guia_aplicable: totalWorkers > 50 ? 'III' : 'II',
        porcentaje_completado: 0,
        cumple_minimo: false
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 animate-spin text-teal-600" />
      </div>
    );
  }

  const { 
    total_trabajadores = 50, 
    cuestionarios_completados = 0, 
    muestra_minima = 50,
    guia_aplicable = 'II',
    porcentaje_completado = 0,
    cumple_minimo = false
  } = participationData || {};

  const progressPercent = Math.min((cuestionarios_completados / muestra_minima) * 100, 100);
  const isGuiaII = guia_aplicable === 'II';

  return (
    <div className="space-y-4">
      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
          <ClipboardList className="w-5 h-5" />
          {language === 'es' 
            ? `Guía ${guia_aplicable}: Diagnóstico de Riesgo Psicosocial` 
            : `Guide ${guia_aplicable}: Psychosocial Risk Diagnosis`}
        </h4>
        <p className="text-sm text-blue-700">
          {isGuiaII 
            ? (language === 'es' 
                ? 'Para empresas de 16 a 50 trabajadores. Se requiere que el 100% de los trabajadores completen el cuestionario.' 
                : 'For companies with 16-50 workers. 100% of workers must complete the questionnaire.')
            : (language === 'es'
                ? 'Para empresas de más de 50 trabajadores. Se requiere una muestra mínima calculada con la fórmula oficial de la NOM-035.'
                : 'For companies with 50+ workers. A minimum sample calculated with the official NOM-035 formula is required.')}
        </p>
      </div>

      {/* Participation stats */}
      <div className="bg-white border rounded-lg p-4 space-y-4">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-gray-700">
            {language === 'es' ? 'Progreso de participación' : 'Participation progress'}
          </span>
          <span className={`text-sm font-bold ${cumple_minimo ? 'text-green-600' : 'text-amber-600'}`}>
            {cuestionarios_completados} / {muestra_minima} 
            <span className="text-gray-400 font-normal ml-1">
              ({progressPercent.toFixed(0)}%)
            </span>
          </span>
        </div>

        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div 
            className={`h-3 rounded-full transition-all duration-500 ${
              cumple_minimo ? 'bg-green-500' : progressPercent > 50 ? 'bg-amber-500' : 'bg-red-400'
            }`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-3 gap-3 pt-2">
          <div className="text-center p-2 bg-gray-50 rounded">
            <p className="text-lg font-bold text-gray-800">{total_trabajadores}</p>
            <p className="text-xs text-gray-500">
              {language === 'es' ? 'Total trabajadores' : 'Total workers'}
            </p>
          </div>
          <div className="text-center p-2 bg-gray-50 rounded">
            <p className="text-lg font-bold text-teal-600">{muestra_minima}</p>
            <p className="text-xs text-gray-500">
              {language === 'es' ? 'Mínimo requerido' : 'Minimum required'}
            </p>
          </div>
          <div className="text-center p-2 bg-gray-50 rounded">
            <p className={`text-lg font-bold ${cumple_minimo ? 'text-green-600' : 'text-amber-600'}`}>
              {cuestionarios_completados}
            </p>
            <p className="text-xs text-gray-500">
              {language === 'es' ? 'Completados' : 'Completed'}
            </p>
          </div>
        </div>
      </div>

      {/* Status message */}
      {cumple_minimo ? (
        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
          <p className="text-sm text-green-700 flex items-center gap-2">
            <Check className="w-5 h-5" />
            <span className="font-medium">
              {language === 'es' 
                ? '¡Participación mínima alcanzada! Puedes continuar al siguiente paso.' 
                : 'Minimum participation reached! You can proceed to the next step.'}
            </span>
          </p>
        </div>
      ) : (
        <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 space-y-3">
          <p className="text-sm text-amber-800 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>
              {language === 'es' 
                ? `Faltan ${muestra_minima - cuestionarios_completados} cuestionarios para alcanzar el mínimo requerido.` 
                : `${muestra_minima - cuestionarios_completados} more questionnaires needed to reach minimum.`}
            </span>
          </p>
          
          {/* Exception option for Guide II only */}
          {isGuiaII && cuestionarios_completados > 0 && (
            <div className="pt-2 border-t border-amber-200">
              <button
                onClick={() => setShowException(!showException)}
                className="text-sm text-amber-700 hover:text-amber-900 underline"
              >
                {language === 'es' 
                  ? '¿No puedes alcanzar el 100%? Documentar excepción justificada' 
                  : "Can't reach 100%? Document justified exception"}
              </button>
              
              {showException && (
                <div className="mt-3 space-y-3 p-3 bg-white rounded border border-amber-200">
                  <p className="text-xs text-gray-600">
                    {language === 'es'
                      ? 'Solo para casos extremos (ej: incapacidad médica prolongada, trabajadores sin acceso digital). Generará una alerta en el IPA.'
                      : 'Only for extreme cases (e.g.: prolonged medical leave, workers without digital access). Will generate an IPA alert.'}
                  </p>
                  <div>
                    <Label className="text-sm">
                      {language === 'es' ? 'Justificación de participación incompleta' : 'Justification for incomplete participation'}
                    </Label>
                    <Textarea
                      value={formData.excepcion_justificacion || ''}
                      onChange={(e) => setFormData({ ...formData, excepcion_justificacion: e.target.value })}
                      placeholder={language === 'es' 
                        ? 'Describe las razones operativas justificadas...'
                        : 'Describe the justified operational reasons...'}
                      rows={2}
                      className="mt-1"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={formData.excepcion_aceptada || false}
                      onChange={(e) => setFormData({ ...formData, excepcion_aceptada: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-amber-600"
                    />
                    <span className="text-gray-700">
                      {language === 'es' 
                        ? 'Entiendo que esto generará una alerta amarilla en el IPA' 
                        : 'I understand this will generate a yellow IPA alert'}
                    </span>
                  </label>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Refresh button */}
      <div className="text-center">
        <Button variant="outline" size="sm" onClick={loadParticipationData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          {language === 'es' ? 'Actualizar participación' : 'Refresh participation'}
        </Button>
      </div>
    </div>
  );
};

// ============== EVIDENCIAS COMPONENT ==============
// M8-I: Dashboard de evidencias para auditoría NOM-035

const EvidenciasStep = ({ config, formData, setFormData, empresaId }) => {
  const { language } = useLanguage();
  const [evidenceData, setEvidenceData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Estados de evidencia
  const EVIDENCE_STATUS = {
    EVIDENCIADA: { icon: '✅', label: { es: 'Evidenciada', en: 'Evidenced' }, bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700' },
    PENDIENTE: { icon: '⏳', label: { es: 'Pendiente', en: 'Pending' }, bg: 'bg-white', border: 'border-gray-200', text: 'text-gray-600' },
    EN_PROGRESO: { icon: '🔄', label: { es: 'En progreso', en: 'In progress' }, bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700' },
    VENCIDA: { icon: '🔴', label: { es: 'Vencida sin evidencia', en: 'Overdue without evidence' }, bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700' },
    ATS_SIN_EVIDENCIA: { icon: '🚨', label: { es: 'ATS sin constancia', en: 'STE without proof' }, bg: 'bg-red-100', border: 'border-red-400', text: 'text-red-800' }
  };

  useEffect(() => {
    loadEvidenceData();
  }, [empresaId]);

  const loadEvidenceData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/v5/nom035/${empresaId}/evidence-status`);
      if (response.data.success) {
        setEvidenceData(response.data.data);
      }
    } catch (error) {
      logger.error('Error loading evidence data:', error);
      setEvidenceData({
        actions: [],
        summary: { evidenciada: 0, pendiente: 0, en_progreso: 0, vencida: 0, ats_sin_evidencia: 0 },
        has_blocking_ats: false
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (action) => {
    if (action.es_ats && !action.tiene_evidencia) return EVIDENCE_STATUS.ATS_SIN_EVIDENCIA;
    if (action.tiene_evidencia) return EVIDENCE_STATUS.EVIDENCIADA;
    if (action.status === 'en_progreso') return EVIDENCE_STATUS.EN_PROGRESO;
    if (action.esta_vencida && !action.tiene_evidencia) return EVIDENCE_STATUS.VENCIDA;
    return EVIDENCE_STATUS.PENDIENTE;
  };

  const openActionPlan = (actionId, isAts = false) => {
    const baseUrl = '/action-plans';
    const params = new URLSearchParams();
    if (actionId) params.set('action', actionId);
    if (isAts) params.set('template', 'ats-canalizacion');
    params.set('panel', 'evidence');
    window.open(`${baseUrl}?${params.toString()}`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 animate-spin text-teal-600" />
      </div>
    );
  }

  const { actions = [], summary = {}, has_blocking_ats = false } = evidenceData || {};
  const totalActions = actions.length;

  // Ordenar: ATS primero, luego vencidas, luego pendientes, luego evidenciadas
  const sortedActions = [...actions].sort((a, b) => {
    const order = { ATS_SIN_EVIDENCIA: 0, VENCIDA: 1, PENDIENTE: 2, EN_PROGRESO: 3, EVIDENCIADA: 4 };
    const statusA = getStatusConfig(a);
    const statusB = getStatusConfig(b);
    const orderA = Object.keys(EVIDENCE_STATUS).indexOf(Object.keys(EVIDENCE_STATUS).find(k => EVIDENCE_STATUS[k] === statusA));
    const orderB = Object.keys(EVIDENCE_STATUS).indexOf(Object.keys(EVIDENCE_STATUS).find(k => EVIDENCE_STATUS[k] === statusB));
    return orderA - orderB;
  });

  return (
    <div className="space-y-4">
      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
          <FileText className="w-5 h-5" />
          {language === 'es' ? 'Evidencias Documentales' : 'Documentary Evidence'}
        </h4>
        <p className="text-sm text-blue-700">
          {language === 'es' 
            ? 'Este paso muestra el estado de las evidencias de todas las acciones NOM-035. Las evidencias se cargan directamente en cada acción dentro de Planes de Acción.'
            : 'This step shows the evidence status of all NOM-035 actions. Evidence is uploaded directly in each action within Action Plans.'}
        </p>
      </div>

      {/* Summary counters */}
      <div className="grid grid-cols-5 gap-2">
        <div className="text-center p-2 bg-green-50 rounded border border-green-200">
          <p className="text-lg font-bold text-green-700">{summary.evidenciada || 0}</p>
          <p className="text-xs text-green-600">✅ {language === 'es' ? 'Evidenciadas' : 'Evidenced'}</p>
        </div>
        <div className="text-center p-2 bg-white rounded border border-gray-200">
          <p className="text-lg font-bold text-gray-700">{summary.pendiente || 0}</p>
          <p className="text-xs text-gray-500">⏳ {language === 'es' ? 'Pendientes' : 'Pending'}</p>
        </div>
        <div className="text-center p-2 bg-blue-50 rounded border border-blue-200">
          <p className="text-lg font-bold text-blue-700">{summary.en_progreso || 0}</p>
          <p className="text-xs text-blue-600">🔄 {language === 'es' ? 'En progreso' : 'In progress'}</p>
        </div>
        <div className="text-center p-2 bg-red-50 rounded border border-red-200">
          <p className="text-lg font-bold text-red-700">{summary.vencida || 0}</p>
          <p className="text-xs text-red-600">🔴 {language === 'es' ? 'Vencidas' : 'Overdue'}</p>
        </div>
        <div className="text-center p-2 bg-red-100 rounded border border-red-400">
          <p className="text-lg font-bold text-red-800">{summary.ats_sin_evidencia || 0}</p>
          <p className="text-xs text-red-700">🚨 {language === 'es' ? 'ATS' : 'STE'}</p>
        </div>
      </div>

      {/* Blocking warning for ATS */}
      {has_blocking_ats && (
        <div className="p-4 bg-red-100 rounded-lg border border-red-400">
          <p className="text-sm text-red-800 flex items-center gap-2 font-medium">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            {language === 'es' 
              ? `Hay ${summary.ats_sin_evidencia} canalización(es) de ATS sin constancia. Cárgalas para poder continuar.`
              : `There are ${summary.ats_sin_evidencia} STE canalization(s) without proof. Upload them to continue.`}
          </p>
        </div>
      )}

      {/* Warning for overdue (non-blocking) */}
      {!has_blocking_ats && (summary.vencida || 0) > 0 && (
        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
          <p className="text-sm text-amber-700 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {language === 'es' 
              ? `Hay ${summary.vencida} acción(es) vencida(s) sin evidencia. Esto afectará tu IPA.`
              : `There are ${summary.vencida} overdue action(s) without evidence. This will affect your IPA.`}
          </p>
        </div>
      )}

      {/* Actions table */}
      {totalActions === 0 ? (
        <div className="p-4 bg-gray-50 rounded-lg border text-center">
          <p className="text-gray-500">
            {language === 'es' 
              ? 'No hay acciones NOM-035 registradas. Crea acciones en el Paso 7 (Programa de Atención).'
              : 'No NOM-035 actions registered. Create actions in Step 7 (Attention Program).'}
          </p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden max-h-64 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-600 w-10">#</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">
                  {language === 'es' ? 'Acción' : 'Action'}
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 w-24">
                  {language === 'es' ? 'Vencimiento' : 'Due date'}
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 w-32">
                  {language === 'es' ? 'Estado' : 'Status'}
                </th>
                <th className="px-3 py-2 text-center font-medium text-gray-600 w-24">
                  {language === 'es' ? 'Evidencia' : 'Evidence'}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedActions.map((action, idx) => {
                const statusConfig = getStatusConfig(action);
                return (
                  <tr key={action.id || idx} className={`border-t ${statusConfig.bg} ${statusConfig.border}`}>
                    <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span>{statusConfig.icon}</span>
                        <span className={`${action.tiene_evidencia ? '' : ''}`}>
                          {action.titulo || action.descripcion}
                        </span>
                        {action.es_ats && (
                          <span className="text-xs bg-red-200 text-red-800 px-1.5 py-0.5 rounded font-medium">
                            ATS
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      {action.fecha_vencimiento 
                        ? new Date(action.fecha_vencimiento).toLocaleDateString()
                        : '-'}
                    </td>
                    <td className={`px-3 py-2 ${statusConfig.text} text-xs font-medium`}>
                      {statusConfig.label[language] || statusConfig.label.es}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {action.tiene_evidencia ? (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-green-600 hover:text-green-800 h-7 px-2"
                          onClick={() => openActionPlan(action.id)}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          {language === 'es' ? 'Ver' : 'View'}
                        </Button>
                      ) : (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className={`h-7 px-2 ${action.es_ats ? 'border-red-400 text-red-700 hover:bg-red-50' : ''}`}
                          onClick={() => openActionPlan(action.id, action.es_ats)}
                        >
                          <Upload className="w-3 h-3 mr-1" />
                          {language === 'es' ? 'Cargar' : 'Upload'}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Success message if all evidenced */}
      {totalActions > 0 && (summary.evidenciada || 0) === totalActions && (
        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
          <p className="text-sm text-green-700 flex items-center gap-2">
            <Check className="w-5 h-5" />
            <span className="font-medium">
              {language === 'es' 
                ? '¡Todas las acciones tienen evidencia! Puedes continuar al siguiente paso.' 
                : 'All actions have evidence! You can proceed to the next step.'}
            </span>
          </p>
        </div>
      )}

      {/* Refresh button */}
      <div className="text-center">
        <Button variant="outline" size="sm" onClick={loadEvidenceData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          {language === 'es' ? 'Actualizar estado' : 'Refresh status'}
        </Button>
      </div>
    </div>
  );
};

// ============== PROGRAMA DE ATENCIÓN COMPONENT ==============
// M8-H: Componente para el Programa de Atención NOM-035

const ProgramaAtencionStep = ({ config, formData, setFormData, empresaId }) => {
  const { language } = useLanguage();
  const [programData, setProgramData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nom035Actions, setNom035Actions] = useState([]);

  // Generar texto de dominios de alto riesgo
  const formatHighRiskDomains = (domains) => {
    if (!domains || domains.length === 0) {
      return language === 'es' 
        ? 'el mantenimiento del entorno organizacional favorable ya existente'
        : 'maintaining the existing favorable organizational environment';
    }
    
    if (domains.length === 1) return domains[0];
    if (domains.length === 2) return `${domains[0]} ${language === 'es' ? 'y' : 'and'} ${domains[1]}`;
    
    const lastDomain = domains.pop();
    return `${domains.join(', ')} ${language === 'es' ? 'y' : 'and'} ${lastDomain}`;
  };

  // Generar texto sugerido de objetivos
  const generateSuggestedObjectives = (data) => {
    const nombreEmpresa = config?.nombre_centro_trabajo || '[Nombre de la Empresa]';
    const añoCiclo = new Date().getFullYear();
    const nEvaluados = data?.cuestionarios_completados || 0;
    const dominiosRiesgo = formatHighRiskDomains(data?.dominios_alto_riesgo || []);
    const responsable = config?.nombre_responsable || '[Responsable NOM-035]';
    const cargo = config?.cargo_responsable || '';
    const fechaReevaluacion = `31 de diciembre de ${añoCiclo + 2}`;

    return language === 'es' 
      ? `${nombreEmpresa} establece el presente Programa de Atención con el objetivo de prevenir, controlar y reducir los factores de riesgo psicosocial identificados durante el ciclo NOM-035 ${añoCiclo}, en los que participaron ${nEvaluados} trabajadores.

Los dominios identificados con mayor nivel de riesgo son: ${dominiosRiesgo}. Las acciones del presente programa se orientan prioritariamente a estos dominios, sin descuidar la atención a los factores clasificados en nivel de riesgo medio.

El responsable del seguimiento y cumplimiento del programa es ${responsable}${cargo ? `, ${cargo}` : ''}, con revisión periódica al menos cada seis meses y reevaluación completa antes del ${fechaReevaluacion}.`
      : `${nombreEmpresa} establishes this Attention Program with the objective of preventing, controlling, and reducing psychosocial risk factors identified during the NOM-035 cycle ${añoCiclo}, in which ${nEvaluados} workers participated.

The domains identified with the highest risk level are: ${dominiosRiesgo}. The actions of this program are primarily oriented towards these domains, without neglecting attention to medium-risk factors.

The person responsible for monitoring and compliance is ${responsable}${cargo ? `, ${cargo}` : ''}, with periodic review at least every six months and complete re-evaluation before ${fechaReevaluacion}.`;
  };

  useEffect(() => {
    loadProgramData();
  }, [empresaId]);

  const loadProgramData = async () => {
    try {
      setLoading(true);
      
      // Cargar datos del programa y acciones NOM-035
      const [programResponse, actionsResponse] = await Promise.all([
        axios.get(`/v5/nom035/${empresaId}/program-data`),
        axios.get(`/v5/action-plans/nom035/${empresaId}`)
      ]);
      
      if (programResponse.data.success) {
        const data = programResponse.data.data;
        setProgramData(data);
        
        // Pre-llenar campos si están vacíos
        if (!formData.nombre_programa) {
          const nombreEmpresa = config?.nombre_centro_trabajo || 'Empresa';
          const añoCiclo = new Date().getFullYear();
          setFormData(prev => ({
            ...prev,
            nombre_programa: `Programa de Atención a Factores de Riesgo Psicosocial — ${nombreEmpresa} · Ciclo ${añoCiclo}`
          }));
        }
        
        if (!formData.objetivos_programa) {
          setFormData(prev => ({
            ...prev,
            objetivos_programa: generateSuggestedObjectives(data)
          }));
        }
      }
      
      if (actionsResponse.data.success) {
        setNom035Actions(actionsResponse.data.data || []);
      }
      
    } catch (error) {
      logger.error('Error loading program data:', error);
      // Fallback
      setProgramData({
        cuestionarios_completados: 0,
        dominios_alto_riesgo: [],
        dominios_medio_riesgo: []
      });
      setNom035Actions([]);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityStyle = (priority, status) => {
    if (status === 'completado') return 'bg-gray-50 text-gray-400 line-through';
    switch (priority) {
      case 'urgente':
      case 'vencida': return 'bg-red-50 text-red-800 font-medium';
      case 'alta': return 'bg-orange-50 text-orange-800';
      case 'media': return 'bg-yellow-50 text-yellow-800';
      default: return 'bg-white';
    }
  };

  const getPriorityBadge = (priority, status) => {
    if (status === 'completado') return { icon: '✅', label: language === 'es' ? 'Completada' : 'Completed' };
    switch (priority) {
      case 'urgente': return { icon: '🔴', label: language === 'es' ? 'Urgente' : 'Urgent' };
      case 'alta': return { icon: '🟠', label: language === 'es' ? 'Alta' : 'High' };
      case 'media': return { icon: '🟡', label: language === 'es' ? 'Media' : 'Medium' };
      default: return { icon: '🟢', label: language === 'es' ? 'Baja' : 'Low' };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 animate-spin text-teal-600" />
      </div>
    );
  }

  const hasActions = nom035Actions.length > 0;

  return (
    <div className="space-y-4">
      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
          <ClipboardList className="w-5 h-5" />
          {language === 'es' ? 'Programa de Atención' : 'Attention Program'}
        </h4>
        <p className="text-sm text-blue-700">
          {language === 'es' 
            ? 'Este programa formaliza las acciones preventivas para atender los factores de riesgo identificados. Los campos se pre-llenan con los datos del diagnóstico.'
            : 'This program formalizes preventive actions to address identified risk factors. Fields are pre-filled with diagnosis data.'}
        </p>
      </div>

      {/* Nombre del programa */}
      <div>
        <Label>{language === 'es' ? 'Nombre del programa' : 'Program name'} *</Label>
        <Input 
          value={formData.nombre_programa || ''} 
          onChange={(e) => setFormData({ ...formData, nombre_programa: e.target.value })}
          maxLength={200}
          className="mt-1"
        />
      </div>

      {/* Objetivos */}
      <div>
        <div className="flex justify-between items-center mb-1">
          <Label>{language === 'es' ? 'Objetivos' : 'Objectives'} *</Label>
          <Button 
            variant="link" 
            size="sm" 
            className="text-blue-600 p-0 h-auto"
            onClick={() => {
              if (window.confirm(language === 'es' 
                ? '¿Restaurar el texto sugerido? Se perderán los cambios actuales.' 
                : 'Restore suggested text? Current changes will be lost.')) {
                setFormData({ ...formData, objetivos_programa: generateSuggestedObjectives(programData) });
              }
            }}
          >
            <RefreshCw className="w-3 h-3 mr-1" />
            {language === 'es' ? 'Restaurar texto sugerido' : 'Restore suggested text'}
          </Button>
        </div>
        <Textarea 
          value={formData.objetivos_programa || ''} 
          onChange={(e) => setFormData({ ...formData, objetivos_programa: e.target.value })}
          rows={6}
          className="text-sm"
        />
      </div>

      {/* Acciones preventivas */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <Label>{language === 'es' ? 'Acciones preventivas' : 'Preventive actions'} *</Label>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => window.open('/action-plans?filter=NOM-035', '_blank')}
          >
            <ExternalLink className="w-3 h-3 mr-1" />
            {language === 'es' ? 'Ir a Planes de Acción' : 'Go to Action Plans'}
          </Button>
        </div>
        
        {!hasActions ? (
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-sm text-amber-800 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>
                {language === 'es' 
                  ? 'No hay acciones con etiqueta NOM-035. Debes crear al menos una acción en Planes de Acción para poder continuar.'
                  : 'No actions with NOM-035 tag. You must create at least one action in Action Plans to continue.'}
              </span>
            </p>
            <Button 
              variant="default" 
              size="sm" 
              className="mt-3"
              onClick={() => window.open('/action-plans?create=true&tag=NOM-035', '_blank')}
            >
              {language === 'es' ? 'Crear acción NOM-035' : 'Create NOM-035 action'}
            </Button>
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">#</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">
                    {language === 'es' ? 'Acción' : 'Action'}
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">
                    {language === 'es' ? 'Dominio' : 'Domain'}
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">
                    {language === 'es' ? 'Resp.' : 'Resp.'}
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">
                    {language === 'es' ? 'Vencimiento' : 'Due date'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {nom035Actions.map((action, idx) => {
                  const priority = getPriorityBadge(action.prioridad, action.status);
                  return (
                    <tr key={action.id || idx} className={`border-t ${getPriorityStyle(action.prioridad, action.status)}`}>
                      <td className="px-3 py-2">{idx + 1}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span>{priority.icon}</span>
                          <span>{action.titulo || action.descripcion}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-gray-600">{action.dominio || '-'}</td>
                      <td className="px-3 py-2 text-gray-600">{action.responsable_nombre || '-'}</td>
                      <td className="px-3 py-2 text-gray-600">
                        {action.fecha_vencimiento 
                          ? new Date(action.fecha_vencimiento).toLocaleDateString()
                          : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="px-3 py-2 bg-gray-50 text-xs text-gray-500 border-t">
              {language === 'es' 
                ? `${nom035Actions.length} acción(es) con etiqueta NOM-035`
                : `${nom035Actions.length} action(s) with NOM-035 tag`}
            </div>
          </div>
        )}
      </div>

      {/* Refresh button */}
      <div className="text-center">
        <Button variant="outline" size="sm" onClick={loadProgramData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          {language === 'es' ? 'Actualizar datos' : 'Refresh data'}
        </Button>
      </div>
    </div>
  );
};

// ============== MAIN COMPONENT ==============

const NOM035Panel = ({ sucursalId }) => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const texts = TEXTS[language] || TEXTS.es;
  
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [config, setConfig] = useState(null);
  const [nextActions, setNextActions] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [programStats, setProgramStats] = useState({ total: 0, evidenciadas: 0, enProgreso: 0, vencidas: 0 });
  const [buzonStats, setBuzonStats] = useState({ total_recibidos: 0, pendientes: 0, atendidos: 0 });
  const [showConfigWizard, setShowConfigWizard] = useState(false);
  const [showGuide1Dialog, setShowGuide1Dialog] = useState(false);
  const [showGuide3Dialog, setShowGuide3Dialog] = useState(false);
  const [showProgramaDialog, setShowProgramaDialog] = useState(false);
  const [showExpedienteDialog, setShowExpedienteDialog] = useState(false);
  const [showReportsDialog, setShowReportsDialog] = useState(false);
  const [showBuzonEmpleadoDialog, setShowBuzonEmpleadoDialog] = useState(false);
  const [showBuzonAdminDialog, setShowBuzonAdminDialog] = useState(false);
  const [showATSReferralDialog, setShowATSReferralDialog] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (user?.empresa_id) {
      loadAllData();
    }
  }, [user, sucursalId]);

  const loadAllData = async () => {
    setLoading(true);
    const sucParam = sucursalId ? `&sucursal_id=${sucursalId}` : '';
    const sucParamFirst = sucursalId ? `?sucursal_id=${sucursalId}` : '';
    try {
      const [dashRes, actionsRes, alertsRes, programRes, buzonRes] = await Promise.all([
        axios.get(`/v5/nom035/${user.empresa_id}/dashboard?lang=${language}${sucParam}`),
        axios.get(`/v5/nom035/${user.empresa_id}/next-actions?lang=${language}${sucParam}`),
        axios.get(`/v5/nom035/${user.empresa_id}/guide1/alerts?current_user_role=admin${sucParam}`),
        axios.get(`/v5/nom035/${user.empresa_id}/programa-atencion?lang=${language}${sucParam}`),
        axios.get(`/v5/nom035/${user.empresa_id}/buzon/stats${sucParamFirst}`)
      ]);
      
      if (dashRes.data.success) {
        setDashboard(dashRes.data.data);
      }
      if (actionsRes.data.success) {
        setNextActions(actionsRes.data.data.actions || []);
      }
      if (alertsRes.data.success) {
        setAlerts(alertsRes.data.data.alertas || []);
      }
      if (programRes.data.success) {
        setProgramStats(programRes.data.data.stats || { total: 0, evidenciadas: 0, enProgreso: 0, vencidas: 0 });
      }
      if (buzonRes.data.success && buzonRes.data.data.stats) {
        setBuzonStats(buzonRes.data.data.stats);
      }
      
      // Load config
      const configRes = await axios.get(`/v5/nom035/${user.empresa_id}/config`);
      if (configRes.data.success) {
        setConfig(configRes.data.data);
      }
    } catch (error) {
      logger.error('Error loading NOM-035 data:', error);
    } finally {
      setLoading(false);
    }
  };

  const isStepCompleted = (stepKey) => config?.pasos_completados?.[stepKey] === true;

  const calculateExpedienteStatus = () => {
    if (!config?.pasos_completados) return { complete: 0, incomplete: 10 };
    const pasos = config.pasos_completados;
    let complete = 0;
    EXPEDIENTE_SECTIONS.forEach(section => {
      if (pasos[section.step]) complete++;
    });
    return { complete, incomplete: 10 - complete };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  const ipaScore = dashboard?.ipa?.score || config?.ipa_score || 0;
  const ipaStatus = getIPAStatus(ipaScore);
  const ipaColors = IPA_COLORS[ipaStatus];
  const completedSteps = Object.values(config?.pasos_completados || {}).filter(v => v === true).length;
  const expedienteStatus = calculateExpedienteStatus();
  const guideApplied = dashboard?.heatmap_dominios?.some(d => d.total_respondentes > 0);

  // Not configured state
  if (!dashboard?.configured) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">{texts.title}</h2>
          <p className="text-sm text-gray-500">{texts.subtitle}</p>
        </div>
        
        <Card className="border-dashed border-2">
          <CardContent className="py-12 text-center">
            <Shield className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {language === 'es' ? 'Módulo NOM-035 no configurado' : 'NOM-035 module not configured'}
            </h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              {language === 'es' 
                ? 'Para configurar el módulo NOM-035, ve al menú de Configuración > NOM-035 en el panel de administración.'
                : 'To configure the NOM-035 module, go to Configuration > NOM-035 in the admin panel.'}
            </p>
            <div className="flex items-center justify-center gap-2 text-teal-600">
              <Settings className="w-5 h-5" />
              <span className="font-medium">
                {language === 'es' ? 'Configuración → NOM-035' : 'Configuration → NOM-035'}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ============== DASHBOARD PRINCIPAL V2 (M8-K Rediseño) ==============
  return (
    <div className="space-y-6">
      {/* Dashboard V2 con nuevo diseño */}
      <NOM035DashboardV2
        empresaId={user.empresa_id}
        onOpenConfig={() => setShowConfigWizard(true)}
        onOpenGuide1={() => setShowGuide1Dialog(true)}
        onOpenGuide3={() => setShowGuide3Dialog(true)}
        onOpenPrograma={() => setShowProgramaDialog(true)}
        onOpenExpediente={() => setShowExpedienteDialog(true)}
        onOpenReports={(reportId) => {
          setShowReportsDialog(true);
          // Could pass reportId to pre-select
        }}
        onOpenBuzonAdmin={() => setShowBuzonAdminDialog(true)}
        onOpenBuzonEmpleado={() => setShowBuzonEmpleadoDialog(true)}
        onOpenATSReferral={() => {
          // Open ATS Referral management dialog
          setShowATSReferralDialog(true);
        }}
      />

      {/* Dialogs */}
      <ConfigWizardDialog 
        open={showConfigWizard}
        onOpenChange={setShowConfigWizard}
        empresaId={user.empresa_id}
        onComplete={loadAllData}
        texts={texts}
        initialStep={currentStep}
      />
      
      <Guide1Dialog 
        open={showGuide1Dialog}
        onOpenChange={setShowGuide1Dialog}
        empresaId={user.empresa_id}
        userId={user.id}
        onComplete={loadAllData}
        texts={texts}
      />
      
      <Guide3Dialog 
        open={showGuide3Dialog}
        onOpenChange={setShowGuide3Dialog}
        empresaId={user.empresa_id}
        userId={user.id}
        onComplete={loadAllData}
        texts={texts}
      />
      
      <ProgramaAtencionM8M 
        open={showProgramaDialog}
        onOpenChange={setShowProgramaDialog}
        empresaId={user.empresa_id}
        onComplete={loadAllData}
      />
      
      <ExpedienteDialog 
        open={showExpedienteDialog}
        onOpenChange={setShowExpedienteDialog}
        empresaId={user.empresa_id}
      />
      
      <ReportsDialog 
        open={showReportsDialog}
        onOpenChange={setShowReportsDialog}
        empresaId={user.empresa_id}
      />
      
      <BuzonEmpleadoDialog 
        open={showBuzonEmpleadoDialog}
        onOpenChange={setShowBuzonEmpleadoDialog}
        empresaId={user.empresa_id}
      />
      
      <BuzonAdminDialog 
        open={showBuzonAdminDialog}
        onOpenChange={setShowBuzonAdminDialog}
        empresaId={user.empresa_id}
        onUpdate={loadAllData}
      />
      
      {/* M8-L: ATS Referral Management Dialog - Nuevo componente con formulario estructurado */}
      <CanalizacionATSM8L
        open={showATSReferralDialog}
        onOpenChange={setShowATSReferralDialog}
        empresaId={user.empresa_id}
        onComplete={loadAllData}
      />
    </div>
  );
};

// Helper function
const calculateDaysUntil = (dateString) => {
  if (!dateString) return 0;
  const target = new Date(dateString);
  const now = new Date();
  const diff = Math.ceil((target - now) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff);
};

// ============== GUIDE 1 DIALOG (ATS) ==============
const Guide1Dialog = ({ open, onOpenChange, empresaId, userId, onComplete, texts }) => {
  const { language } = useLanguage();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      loadQuestions();
    }
  }, [open]);

  const loadQuestions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/v5/nom035/${empresaId}/guide1/questions?lang=${language}`);
      if (res.data.success) {
        setQuestions(res.data.data.questions);
        // Initialize answers
        const init = {};
        res.data.data.questions.forEach(q => { init[q.id] = null; });
        setAnswers(init);
      }
    } catch (error) {
      toast.error('Error loading questions');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Check all answered
    const allAnswered = Object.values(answers).every(v => v !== null);
    if (!allAnswered) {
      toast.error(language === 'es' ? 'Responde todas las preguntas' : 'Answer all questions');
      return;
    }

    setSubmitting(true);
    try {
      const respuestas = {};
      Object.entries(answers).forEach(([k, v]) => { respuestas[k] = v; });
      
      const res = await axios.post(`/v5/nom035/${empresaId}/guide1/submit?user_id=${userId}`, { respuestas });
      if (res.data.success) {
        toast.success(language === 'es' ? 'Respuestas enviadas' : 'Answers submitted');
        onOpenChange(false);
        onComplete();
      }
    } catch (error) {
      toast.error('Error submitting answers');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-orange-500" />
            {texts.guide1Title}
          </DialogTitle>
          <DialogDescription>{texts.confidential}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {questions.map((q, idx) => (
              <div key={q.id} className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium mb-3">{idx + 1}. {q.text}</p>
                <div className="flex gap-4">
                  <Button
                    variant={answers[q.id] === true ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setAnswers({ ...answers, [q.id]: true })}
                  >
                    {language === 'es' ? 'Sí' : 'Yes'}
                  </Button>
                  <Button
                    variant={answers[q.id] === false ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setAnswers({ ...answers, [q.id]: false })}
                  >
                    No
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {language === 'es' ? 'Cancelar' : 'Cancel'}
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
            {texts.submitAnswers}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============== GUIDE 3 DIALOG (72 items) ==============
const Guide3Dialog = ({ open, onOpenChange, empresaId, userId, onComplete, texts }) => {
  const { language } = useLanguage();
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentDominio, setCurrentDominio] = useState(0);

  useEffect(() => {
    if (open) {
      loadQuestions();
    }
  }, [open]);

  const loadQuestions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/v5/nom035/${empresaId}/guide3/questions?lang=${language}`);
      if (res.data.success) {
        setData(res.data.data);
        // Initialize answers
        const init = {};
        res.data.data.dominios.forEach(d => {
          d.items.forEach(item => { init[item.id] = null; });
        });
        setAnswers(init);
      }
    } catch (error) {
      toast.error('Error loading questions');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const allAnswered = Object.values(answers).every(v => v !== null);
    if (!allAnswered) {
      toast.error(language === 'es' ? 'Responde todas las preguntas' : 'Answer all questions');
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`/v5/nom035/${empresaId}/guide3/submit?user_id=${userId}`, { respuestas: answers });
      if (res.data.success) {
        toast.success(language === 'es' ? 'Cuestionario completado' : 'Questionnaire completed');
        onOpenChange(false);
        onComplete();
      }
    } catch (error) {
      toast.error('Error submitting answers');
    } finally {
      setSubmitting(false);
    }
  };

  const currentDominioData = data?.dominios?.[currentDominio];
  const totalDominios = data?.dominios?.length || 0;
  const answeredInCurrent = currentDominioData?.items?.filter(i => answers[i.id] !== null).length || 0;
  const totalInCurrent = currentDominioData?.items?.length || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-teal-600" />
            {texts.guide3Title}
          </DialogTitle>
          <DialogDescription>
            {data?.total_items || 72} {language === 'es' ? 'preguntas' : 'questions'} • {texts.confidential}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <>
            {/* Domain tabs */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {data?.dominios?.map((d, idx) => (
                <Button
                  key={idx}
                  variant={currentDominio === idx ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCurrentDominio(idx)}
                  className="whitespace-nowrap"
                >
                  {idx + 1}. {d.nombre.substring(0, 15)}...
                </Button>
              ))}
            </div>

            {/* Current domain questions */}
            <div className="space-y-3 max-h-64 overflow-y-auto border rounded-lg p-4">
              <h4 className="font-medium text-sm text-gray-700 mb-2">
                {currentDominioData?.nombre} ({answeredInCurrent}/{totalInCurrent})
              </h4>
              {currentDominioData?.items?.map((item) => (
                <div key={item.id} className="p-3 bg-gray-50 rounded">
                  <p className="text-sm mb-2">{item.id}. {item.texto}</p>
                  <div className="flex gap-1 flex-wrap">
                    {data.escala.map(opt => (
                      <Button
                        key={opt.value}
                        variant={answers[item.id] === opt.value ? 'default' : 'outline'}
                        size="sm"
                        className="text-xs"
                        onClick={() => setAnswers({ ...answers, [item.id]: opt.value })}
                      >
                        {language === 'es' ? opt.label_es : opt.label_en}
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between pt-2">
              <Button
                variant="outline"
                disabled={currentDominio === 0}
                onClick={() => setCurrentDominio(currentDominio - 1)}
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                {language === 'es' ? 'Anterior' : 'Previous'}
              </Button>
              <span className="text-sm text-gray-500">
                {language === 'es' ? 'Dominio' : 'Domain'} {currentDominio + 1} / {totalDominios}
              </span>
              {currentDominio < totalDominios - 1 ? (
                <Button onClick={() => setCurrentDominio(currentDominio + 1)}>
                  {language === 'es' ? 'Siguiente' : 'Next'}
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={submitting}>
                  {submitting && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
                  {texts.submitAnswers}
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ============== CONFIG WIZARD DIALOG ==============
const ConfigWizardDialog = ({ open, onOpenChange, empresaId, onComplete, texts, initialStep = 0 }) => {
  const { language } = useLanguage();
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]); // Para selector de responsable interno
  const [userSearchOpen, setUserSearchOpen] = useState(false); // Para el combobox de usuarios
  const [formData, setFormData] = useState({
    nombre_centro_trabajo: '', numero_trabajadores: '', nombre_representante_legal: '',
    rfc_centro_trabajo: '', fecha_inicio_ciclo: '', 
    // M8-E-REV v2: Nuevos campos de responsable dual
    responsable_tipo: 'interno', // 'interno' o 'externo'
    responsable_user_id: '',
    responsable_externo_nombre: '',
    responsable_externo_email: '',
    cargo_responsable: '',
    declaracion_principios: '',
    compromisos_especificos: '', mecanismo_denuncia: '', 
    metodos_difusion: [], // Array para múltiples métodos
    metodo_difusion_otro: '', // Campo para método personalizado
    tiene_ats: null, // Guía I: ¿Hay casos de ATS?
    numero_ats: 0, // Número de trabajadores con ATS
    acciones_ats: '', // Acciones tomadas para ATS
    excepcion_justificacion: '', // Excepción para Guía II
    excepcion_aceptada: false, // Checkbox de excepción
    metodos_comunicacion: [], // Array para múltiples métodos de comunicación (Paso 9)
    metodo_comunicacion_otro: '', // Campo para método personalizado (Paso 9)
    nombre_programa: '', objetivos_programa: '', acciones_programa: '',
    responsables_programa: '', metodo_comunicacion: ''
  });

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open]);

  // M8-F: Función para obtener el nombre del responsable (interno o externo)
  const getResponsableName = () => {
    if (formData.responsable_tipo === 'externo') {
      return formData.responsable_externo_nombre || '[Responsable NOM-035]';
    } else {
      const selectedUser = companyUsers.find(u => u.id === formData.responsable_user_id);
      if (selectedUser) {
        return `${selectedUser.nombre}${selectedUser.apellido_paterno ? ' ' + selectedUser.apellido_paterno : ''}`;
      }
      return '[Responsable NOM-035]';
    }
  };

  // M8-F: Función para generar textos de política pre-llenados
  const generatePolicyTexts = () => {
    const nombreEmpresa = formData.nombre_centro_trabajo || '[Nombre de la Empresa]';
    const representanteLegal = formData.nombre_representante_legal || '[Representante Legal]';
    const responsableNom = getResponsableName();
    const cargoResponsable = formData.cargo_responsable || '';

    return {
      declaracion_principios: POLICY_TEMPLATES.declaracion_principios(nombreEmpresa, representanteLegal),
      compromisos_especificos: POLICY_TEMPLATES.compromisos_especificos(nombreEmpresa, responsableNom, cargoResponsable),
      mecanismo_denuncia: POLICY_TEMPLATES.mecanismo_denuncia(nombreEmpresa, responsableNom, cargoResponsable)
    };
  };

  // M8-F: Pre-llenar política cuando el usuario llega al paso 3 por primera vez
  useEffect(() => {
    if (currentStep === 2) { // Paso 3 = index 2
      // Solo pre-llenar si los campos están vacíos
      const policyTexts = generatePolicyTexts();
      let updated = false;
      const updates = {};

      if (!formData.declaracion_principios) {
        updates.declaracion_principios = policyTexts.declaracion_principios;
        updated = true;
      }
      if (!formData.compromisos_especificos) {
        updates.compromisos_especificos = policyTexts.compromisos_especificos;
        updated = true;
      }
      if (!formData.mecanismo_denuncia) {
        updates.mecanismo_denuncia = policyTexts.mecanismo_denuncia;
        updated = true;
      }

      if (updated) {
        setFormData(prev => ({ ...prev, ...updates }));
      }
    }
  }, [currentStep, companyUsers]);

  const loadData = async () => {
    try {
      const [configRes, policyRes, empRes, usersRes] = await Promise.all([
        axios.get(`/v5/nom035/${empresaId}/config`),
        axios.get(`/v5/nom035/${empresaId}/policy`),
        axios.get(`/v5/nom035/${empresaId}/employees-list`),
        axios.get(`/companies/employees`) // Para el selector de responsable interno
      ]);
      
      if (configRes.data.success) {
        const c = configRes.data.data;
        setConfig(c);
        setFormData(prev => ({
          ...prev,
          nombre_centro_trabajo: c.nombre_centro_trabajo || '',
          numero_trabajadores: c.numero_trabajadores || '',
          nombre_representante_legal: c.nombre_representante_legal || '',
          rfc_centro_trabajo: c.rfc_centro_trabajo || '',
          fecha_inicio_ciclo: c.fecha_inicio_ciclo ? c.fecha_inicio_ciclo.split('T')[0] : '',
          // M8-E-REV v2: Cargar campos de responsable dual
          responsable_tipo: c.responsable_tipo || 'interno',
          responsable_user_id: c.responsable_user_id || '',
          responsable_externo_nombre: c.responsable_externo_nombre || '',
          responsable_externo_email: c.responsable_externo_email || '',
          cargo_responsable: c.cargo_responsable || ''
        }));
        
        // Set current step based on progress
        const pasos = c.pasos_completados || {};
        let step = 0;
        for (let i = 0; i < STEP_ORDER.length; i++) {
          if (pasos[STEP_ORDER[i]]) step = i + 1;
          else break;
        }
        setCurrentStep(Math.min(step, STEP_ORDER.length - 1));
      }
      
      if (policyRes.data.success) setPolicy(policyRes.data.data);
      if (empRes.data.success) setEmployees(empRes.data.data.employees || []);
      if (usersRes.data.success) setCompanyUsers(usersRes.data.data || []);
    } catch (error) {
      logger.error('Error loading config data:', error);
    }
  };

  const handleSaveStep = async () => {
    setSaving(true);
    const stepKey = STEP_ORDER[currentStep];
    
    try {
      let endpoint = `/v5/nom035/${empresaId}/config/step${currentStep + 1}`;
      let payload = {};
      
      switch (stepKey) {
        case 'DATOS_EMPRESA':
          payload = {
            nombre_centro_trabajo: formData.nombre_centro_trabajo,
            numero_trabajadores: parseInt(formData.numero_trabajadores),
            nombre_representante_legal: formData.nombre_representante_legal,
            rfc_centro_trabajo: formData.rfc_centro_trabajo,
            fecha_inicio_ciclo: new Date(formData.fecha_inicio_ciclo).toISOString()
          };
          break;
        case 'RESPONSABLE':
          // M8-E-REV v2: Nuevo payload con selector dual
          payload = {
            responsable_tipo: formData.responsable_tipo,
            responsable_user_id: formData.responsable_tipo === 'interno' ? formData.responsable_user_id : null,
            responsable_externo_nombre: formData.responsable_tipo === 'externo' ? formData.responsable_externo_nombre : null,
            responsable_externo_email: formData.responsable_tipo === 'externo' ? formData.responsable_externo_email : null,
            cargo_responsable: formData.cargo_responsable || null
          };
          break;
        case 'POLITICA':
          payload = {
            declaracion_principios: formData.declaracion_principios,
            compromisos_especificos: formData.compromisos_especificos,
            mecanismo_denuncia: formData.mecanismo_denuncia
          };
          break;
        case 'DIFUSION':
          // Múltiples métodos de difusión
          payload = { 
            metodos_difusion: formData.metodos_difusion || [],
            metodo_difusion_otro: formData.metodo_difusion_otro || ''
          };
          break;
        case 'GUIA_I':
          // Paso 5: Guía I - ATS
          if (formData.tiene_ats === null) {
            toast.error(language === 'es' ? 'Indica si hay casos de ATS' : 'Indicate if there are STE cases');
            setSaving(false);
            return;
          }
          payload = {
            tiene_ats: formData.tiene_ats,
            numero_ats: formData.tiene_ats ? (formData.numero_ats || 0) : 0,
            acciones_ats: formData.tiene_ats ? (formData.acciones_ats || '') : ''
          };
          break;
        case 'GUIA_II_III':
          // Paso 6: Guía II/III - Diagnóstico de riesgo psicosocial con validación de participación
          payload = {
            excepcion_justificacion: formData.excepcion_justificacion || null,
            excepcion_aceptada: formData.excepcion_aceptada || false
          };
          break;
        case 'PROGRAMA':
          payload = {
            nombre_programa: formData.nombre_programa,
            objetivos: formData.objetivos_programa,
            acciones_preventivas: formData.acciones_programa,
            responsables: formData.responsables_programa
          };
          break;
        case 'COMUNICACION':
          // Múltiples métodos de comunicación (igual que difusión)
          if (!formData.metodos_comunicacion || formData.metodos_comunicacion.length === 0) {
            toast.error(language === 'es' ? 'Selecciona al menos un método de comunicación' : 'Select at least one communication method');
            setSaving(false);
            return;
          }
          payload = { 
            metodos_comunicacion: formData.metodos_comunicacion || [],
            metodo_comunicacion_otro: formData.metodo_comunicacion_otro || ''
          };
          break;
        default:
          payload = {};
      }
      
      const res = await axios.post(endpoint, payload);
      if (res.data.success) {
        toast.success(language === 'es' ? 'Guardado' : 'Saved');
        if (currentStep < STEP_ORDER.length - 1) {
          setCurrentStep(currentStep + 1);
        } else {
          onOpenChange(false);
          onComplete();
        }
        loadData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const isStepCompleted = (stepKey) => config?.pasos_completados?.[stepKey] === true;
  const currentStepKey = STEP_ORDER[currentStep];
  const StepIcon = STEP_ICONS[currentStepKey];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-teal-600" />
            NOM-035 - {language === 'es' ? 'Configuración' : 'Configuration'}
          </DialogTitle>
          <DialogDescription>
            {language === 'es' ? 'Paso' : 'Step'} {currentStep + 1} / 9: {texts.steps[currentStepKey]}
          </DialogDescription>
        </DialogHeader>

        {/* Step progress */}
        <div className="flex items-center gap-1 py-2 overflow-x-auto">
          {STEP_ORDER.map((stepKey, idx) => {
            const completed = isStepCompleted(stepKey);
            const isCurrent = idx === currentStep;
            return (
              <React.Fragment key={stepKey}>
                <button
                  className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                    completed ? 'bg-green-500 text-white' : isCurrent ? 'bg-teal-600 text-white' : 'bg-gray-200 text-gray-700'
                  }`}
                  onClick={() => setCurrentStep(idx)}
                >
                  {completed ? <Check className="w-4 h-4" /> : idx + 1}
                </button>
                {idx < STEP_ORDER.length - 1 && (
                  <div className={`flex-shrink-0 w-4 h-0.5 ${completed ? 'bg-green-500' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step content */}
        <div className="py-4 space-y-4">
          {currentStep === 0 && (
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>{language === 'es' ? 'Nombre del centro de trabajo' : 'Workplace name'} *</Label>
                <Input value={formData.nombre_centro_trabajo} onChange={(e) => setFormData({ ...formData, nombre_centro_trabajo: e.target.value })} />
              </div>
              <div>
                <Label>{language === 'es' ? 'Número de trabajadores' : 'Number of workers'} *</Label>
                <Input type="number" value={formData.numero_trabajadores} onChange={(e) => setFormData({ ...formData, numero_trabajadores: e.target.value })} />
              </div>
              <div>
                <Label>RFC *</Label>
                <Input value={formData.rfc_centro_trabajo} onChange={(e) => setFormData({ ...formData, rfc_centro_trabajo: e.target.value.toUpperCase() })} maxLength={13} />
              </div>
              <div className="col-span-2">
                <Label>{language === 'es' ? 'Representante legal' : 'Legal representative'} *</Label>
                <Input value={formData.nombre_representante_legal} onChange={(e) => setFormData({ ...formData, nombre_representante_legal: e.target.value })} />
              </div>
              <div>
                <Label>{language === 'es' ? 'Fecha inicio ciclo' : 'Cycle start date'} *</Label>
                <Input type="date" value={formData.fecha_inicio_ciclo} onChange={(e) => setFormData({ ...formData, fecha_inicio_ciclo: e.target.value })} />
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div className="space-y-4">
              {/* M8-E-REV v2: Selector dual de responsable */}
              <div className="space-y-3">
                <Label className="text-base font-medium">
                  {language === 'es' ? 'Tipo de responsable' : 'Responsible type'}
                </Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="responsable_tipo"
                      value="interno"
                      checked={formData.responsable_tipo === 'interno'}
                      onChange={() => setFormData({ ...formData, responsable_tipo: 'interno', responsable_externo_nombre: '', responsable_externo_email: '' })}
                      className="w-4 h-4 text-teal-600"
                    />
                    <span className="text-sm">{language === 'es' ? 'Interno (usuario de la plataforma)' : 'Internal (platform user)'}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="responsable_tipo"
                      value="externo"
                      checked={formData.responsable_tipo === 'externo'}
                      onChange={() => setFormData({ ...formData, responsable_tipo: 'externo', responsable_user_id: '' })}
                      className="w-4 h-4 text-teal-600"
                    />
                    <span className="text-sm">{language === 'es' ? 'Externo (persona fuera de la plataforma)' : 'External (person outside platform)'}</span>
                  </label>
                </div>
              </div>

              {formData.responsable_tipo === 'interno' ? (
                <div className="p-4 bg-teal-50 rounded-lg border border-teal-200 space-y-3">
                  <Label>{language === 'es' ? 'Seleccionar usuario' : 'Select user'} *</Label>
                  <Popover open={userSearchOpen} onOpenChange={setUserSearchOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={userSearchOpen}
                        className="w-full justify-between bg-white"
                      >
                        {formData.responsable_user_id
                          ? (() => {
                              const selectedUser = companyUsers.find(u => u.id === formData.responsable_user_id);
                              return selectedUser 
                                ? `${selectedUser.nombre}${selectedUser.apellido_paterno ? ' ' + selectedUser.apellido_paterno : ''} (${selectedUser.email})`
                                : (language === 'es' ? 'Seleccionar empleado...' : 'Select employee...');
                            })()
                          : (language === 'es' ? 'Seleccionar empleado...' : 'Select employee...')}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0" align="start">
                      <Command>
                        <CommandInput placeholder={language === 'es' ? 'Buscar usuario...' : 'Search user...'} />
                        <CommandList>
                          <CommandEmpty>{language === 'es' ? 'No se encontraron usuarios.' : 'No users found.'}</CommandEmpty>
                          <CommandGroup>
                            {[...companyUsers]
                              .sort((a, b) => {
                                const nameA = `${a.nombre || ''} ${a.apellido_paterno || ''}`.toLowerCase();
                                const nameB = `${b.nombre || ''} ${b.apellido_paterno || ''}`.toLowerCase();
                                return nameA.localeCompare(nameB);
                              })
                              .map(u => (
                                <CommandItem
                                  key={u.id}
                                  value={`${u.nombre} ${u.apellido_paterno || ''} ${u.email}`}
                                  onSelect={() => {
                                    setFormData({ ...formData, responsable_user_id: u.id });
                                    setUserSearchOpen(false);
                                  }}
                                >
                                  <Check
                                    className={`mr-2 h-4 w-4 ${
                                      formData.responsable_user_id === u.id ? 'opacity-100' : 'opacity-0'
                                    }`}
                                  />
                                  <div className="flex flex-col">
                                    <span className="font-medium">
                                      {u.nombre}{u.apellido_paterno ? ` ${u.apellido_paterno}` : ''}
                                    </span>
                                    <span className="text-xs text-gray-500">{u.email}</span>
                                  </div>
                                </CommandItem>
                              ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-teal-700">
                    {language === 'es' 
                      ? 'El usuario seleccionado será notificado automáticamente y podrá gestionar los mensajes del buzón.'
                      : 'The selected user will be automatically notified and will be able to manage mailbox messages.'}
                  </p>
                </div>
              ) : (
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-200 space-y-3">
                  <div>
                    <Label>{language === 'es' ? 'Nombre del responsable externo' : 'External responsible name'} *</Label>
                    <Input 
                      value={formData.responsable_externo_nombre} 
                      onChange={(e) => setFormData({ ...formData, responsable_externo_nombre: e.target.value })} 
                      placeholder={language === 'es' ? 'Ej: Dr. Juan Pérez' : 'E.g.: Dr. John Smith'}
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input 
                      type="email" 
                      value={formData.responsable_externo_email} 
                      onChange={(e) => setFormData({ ...formData, responsable_externo_email: e.target.value })} 
                      placeholder="responsable@consultoria.com"
                    />
                  </div>
                  <p className="text-xs text-orange-700">
                    {language === 'es' 
                      ? 'Este responsable externo no tendrá acceso directo a la plataforma. Deberá coordinarse manualmente.'
                      : 'This external responsible will not have direct access to the platform. Must coordinate manually.'}
                  </p>
                </div>
              )}

              <div>
                <Label>{language === 'es' ? 'Cargo (opcional)' : 'Position (optional)'}</Label>
                <Input 
                  value={formData.cargo_responsable} 
                  onChange={(e) => setFormData({ ...formData, cargo_responsable: e.target.value })} 
                  placeholder={language === 'es' ? 'Ej: Gerente de RH' : 'E.g.: HR Manager'}
                />
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-700">
                  <span className="font-medium">{language === 'es' ? '💡 Nota:' : '💡 Note:'}</span>{' '}
                  {language === 'es' 
                    ? 'Los textos han sido pre-llenados según la NOM-035-STPS-2018. Puedes editarlos si lo necesitas.'
                    : 'Texts have been pre-filled according to NOM-035-STPS-2018. You can edit them if needed.'}
                </p>
                <Button 
                  variant="link" 
                  size="sm" 
                  className="text-blue-600 p-0 h-auto mt-1"
                  onClick={() => {
                    const policyTexts = generatePolicyTexts();
                    setFormData(prev => ({
                      ...prev,
                      declaracion_principios: policyTexts.declaracion_principios,
                      compromisos_especificos: policyTexts.compromisos_especificos,
                      mecanismo_denuncia: policyTexts.mecanismo_denuncia
                    }));
                    toast.success(language === 'es' ? 'Textos regenerados' : 'Texts regenerated');
                  }}
                >
                  <RefreshCw className="w-3 h-3 mr-1" />
                  {language === 'es' ? 'Regenerar textos predefinidos' : 'Regenerate predefined texts'}
                </Button>
              </div>
              <div>
                <Label>{language === 'es' ? 'Declaración de principios' : 'Declaration of principles'} *</Label>
                <Textarea 
                  value={formData.declaracion_principios} 
                  onChange={(e) => setFormData({ ...formData, declaracion_principios: e.target.value })} 
                  rows={8}
                  className="text-sm"
                />
              </div>
              <div>
                <Label>{language === 'es' ? 'Compromisos específicos' : 'Specific commitments'} *</Label>
                <Textarea 
                  value={formData.compromisos_especificos} 
                  onChange={(e) => setFormData({ ...formData, compromisos_especificos: e.target.value })} 
                  rows={12}
                  className="text-sm"
                />
              </div>
              <div>
                <Label>{language === 'es' ? 'Mecanismo de denuncia' : 'Complaint mechanism'} *</Label>
                <Textarea 
                  value={formData.mecanismo_denuncia} 
                  onChange={(e) => setFormData({ ...formData, mecanismo_denuncia: e.target.value })} 
                  rows={8}
                  className="text-sm"
                />
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-700">
                  <span className="font-medium">{language === 'es' ? '📋 Nota:' : '📋 Note:'}</span>{' '}
                  {language === 'es' 
                    ? 'Selecciona los métodos que utilizarás para comunicar la política a los trabajadores. Esto solo registra el método de difusión, no envía automáticamente ningún documento.'
                    : 'Select the methods you will use to communicate the policy to workers. This only records the diffusion method, it does not automatically send any document.'}
                </p>
              </div>
              
              <div>
                <Label className="mb-3 block">{language === 'es' ? 'Métodos de difusión' : 'Diffusion methods'} *</Label>
                <div className="space-y-3">
                  {[
                    { value: 'email', label: { es: 'Correo electrónico', en: 'Email' }, desc: { es: 'Enviar por correo a los colaboradores', en: 'Send by email to employees' } },
                    { value: 'cartel', label: { es: 'Cartel físico', en: 'Physical poster' }, desc: { es: 'Colocar en áreas visibles del centro de trabajo', en: 'Place in visible areas of the workplace' } },
                    { value: 'reunion', label: { es: 'Reunión informativa', en: 'Informational meeting' }, desc: { es: 'Presentar en junta con los trabajadores', en: 'Present in a meeting with workers' } },
                    { value: 'intranet', label: { es: 'Intranet / Portal interno', en: 'Intranet / Internal portal' }, desc: { es: 'Publicar en el sistema interno de la empresa', en: 'Publish on the company internal system' } },
                    { value: 'capacitacion', label: { es: 'Capacitación', en: 'Training' }, desc: { es: 'Incluir en sesiones de formación', en: 'Include in training sessions' } },
                  ].map((method) => (
                    <label 
                      key={method.value} 
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        formData.metodos_difusion?.includes(method.value) 
                          ? 'bg-teal-50 border-teal-300' 
                          : 'bg-white border-gray-200 hover:border-teal-200'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.metodos_difusion?.includes(method.value) || false}
                        onChange={(e) => {
                          const current = formData.metodos_difusion || [];
                          if (e.target.checked) {
                            setFormData({ ...formData, metodos_difusion: [...current, method.value] });
                          } else {
                            setFormData({ ...formData, metodos_difusion: current.filter(m => m !== method.value) });
                          }
                        }}
                        className="mt-1 w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                      />
                      <div>
                        <span className="font-medium text-gray-900">{method.label[language] || method.label.es}</span>
                        <p className="text-xs text-gray-500 mt-0.5">{method.desc[language] || method.desc.es}</p>
                      </div>
                    </label>
                  ))}
                  
                  {/* Opción "Otro" */}
                  <label 
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      formData.metodos_difusion?.includes('otro') 
                        ? 'bg-teal-50 border-teal-300' 
                        : 'bg-white border-gray-200 hover:border-teal-200'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.metodos_difusion?.includes('otro') || false}
                      onChange={(e) => {
                        const current = formData.metodos_difusion || [];
                        if (e.target.checked) {
                          setFormData({ ...formData, metodos_difusion: [...current, 'otro'] });
                        } else {
                          setFormData({ ...formData, metodos_difusion: current.filter(m => m !== 'otro'), metodo_difusion_otro: '' });
                        }
                      }}
                      className="mt-1 w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                    />
                    <div className="flex-1">
                      <span className="font-medium text-gray-900">{language === 'es' ? 'Otro método' : 'Other method'}</span>
                      <p className="text-xs text-gray-500 mt-0.5">{language === 'es' ? 'Especifica un método diferente' : 'Specify a different method'}</p>
                    </div>
                  </label>
                  
                  {formData.metodos_difusion?.includes('otro') && (
                    <div className="ml-7">
                      <Input
                        placeholder={language === 'es' ? 'Describe el método de difusión...' : 'Describe the diffusion method...'}
                        value={formData.metodo_difusion_otro || ''}
                        onChange={(e) => setFormData({ ...formData, metodo_difusion_otro: e.target.value })}
                        className="mt-2"
                      />
                    </div>
                  )}
                </div>
                
                {(!formData.metodos_difusion || formData.metodos_difusion.length === 0) && (
                  <p className="text-sm text-amber-600 mt-2">
                    {language === 'es' ? 'Selecciona al menos un método de difusión' : 'Select at least one diffusion method'}
                  </p>
                )}
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  {language === 'es' ? '¿Qué es la Guía I?' : 'What is Guide I?'}
                </h4>
                <p className="text-sm text-amber-700 mb-3">
                  {language === 'es' 
                    ? 'La Guía I identifica a trabajadores que fueron sujetos a Acontecimientos Traumáticos Severos (ATS) durante o con motivo del trabajo. Estos incluyen:'
                    : 'Guide I identifies workers who were subjected to Severe Traumatic Events (STE) during or because of work. These include:'}
                </p>
                <ul className="text-sm text-amber-700 list-disc list-inside space-y-1 mb-3">
                  <li>{language === 'es' ? 'Accidentes graves o mortales' : 'Serious or fatal accidents'}</li>
                  <li>{language === 'es' ? 'Asaltos con violencia' : 'Violent assaults'}</li>
                  <li>{language === 'es' ? 'Secuestro o amenazas' : 'Kidnapping or threats'}</li>
                  <li>{language === 'es' ? 'Presenciar actos violentos' : 'Witnessing violent acts'}</li>
                </ul>
              </div>

              <div className="space-y-3">
                <Label className="text-base font-medium">
                  {language === 'es' 
                    ? '¿Algún trabajador ha experimentado un ATS en los últimos 12 meses?' 
                    : 'Has any worker experienced an STE in the last 12 months?'}
                </Label>
                
                <div className="flex gap-3">
                  <label className={`flex-1 flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                    formData.tiene_ats === false ? 'bg-green-50 border-green-300' : 'bg-white border-gray-200 hover:border-green-200'
                  }`}>
                    <input
                      type="radio"
                      name="tiene_ats"
                      checked={formData.tiene_ats === false}
                      onChange={() => setFormData({ ...formData, tiene_ats: false })}
                      className="w-4 h-4 text-green-600"
                    />
                    <div>
                      <span className="font-medium text-gray-900">{language === 'es' ? 'No' : 'No'}</span>
                      <p className="text-xs text-gray-500">
                        {language === 'es' ? 'No hay casos de ATS reportados' : 'No STE cases reported'}
                      </p>
                    </div>
                  </label>
                  
                  <label className={`flex-1 flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                    formData.tiene_ats === true ? 'bg-amber-50 border-amber-300' : 'bg-white border-gray-200 hover:border-amber-200'
                  }`}>
                    <input
                      type="radio"
                      name="tiene_ats"
                      checked={formData.tiene_ats === true}
                      onChange={() => setFormData({ ...formData, tiene_ats: true })}
                      className="w-4 h-4 text-amber-600"
                    />
                    <div>
                      <span className="font-medium text-gray-900">{language === 'es' ? 'Sí' : 'Yes'}</span>
                      <p className="text-xs text-gray-500">
                        {language === 'es' ? 'Hay casos de ATS que requieren atención' : 'There are STE cases requiring attention'}
                      </p>
                    </div>
                  </label>
                </div>

                {formData.tiene_ats === true && (
                  <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200 space-y-3">
                    <p className="text-sm text-amber-800 font-medium">
                      {language === 'es' 
                        ? 'Debes canalizar a los trabajadores afectados para atención médica/psicológica.' 
                        : 'You must refer affected workers for medical/psychological attention.'}
                    </p>
                    <div>
                      <Label>{language === 'es' ? 'Número de trabajadores afectados' : 'Number of affected workers'}</Label>
                      <Input 
                        type="number" 
                        min="1"
                        value={formData.numero_ats || ''} 
                        onChange={(e) => setFormData({ ...formData, numero_ats: parseInt(e.target.value) || 0 })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>{language === 'es' ? 'Acciones tomadas (opcional)' : 'Actions taken (optional)'}</Label>
                      <Textarea 
                        value={formData.acciones_ats || ''} 
                        onChange={(e) => setFormData({ ...formData, acciones_ats: e.target.value })}
                        placeholder={language === 'es' 
                          ? 'Ej: Se canalizó al trabajador con el servicio médico...' 
                          : 'E.g.: Worker was referred to medical service...'}
                        rows={2}
                        className="mt-1"
                      />
                    </div>
                  </div>
                )}

                {formData.tiene_ats === false && (
                  <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-green-700 flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      {language === 'es' 
                        ? 'Al no haber casos de ATS, puedes continuar al siguiente paso.' 
                        : 'With no STE cases, you can proceed to the next step.'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {currentStep === 5 && (
            <GuiaIIIIIStep 
              config={config}
              formData={formData}
              setFormData={setFormData}
              empresaId={empresaId}
            />
          )}

          {currentStep === 6 && (
            <ProgramaAtencionStep 
              config={config}
              formData={formData}
              setFormData={setFormData}
              empresaId={empresaId}
            />
          )}

          {currentStep === 7 && (
            <EvidenciasStep 
              config={config}
              formData={formData}
              setFormData={setFormData}
              empresaId={empresaId}
            />
          )}

          {currentStep === 8 && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-700">
                  <span className="font-medium">{language === 'es' ? '📢 Nota:' : '📢 Note:'}</span>{' '}
                  {language === 'es' 
                    ? 'Selecciona los métodos que utilizarás para comunicar los resultados del diagnóstico y las acciones del programa a los trabajadores.'
                    : 'Select the methods you will use to communicate the diagnosis results and program actions to workers.'}
                </p>
              </div>
              
              <div>
                <Label className="mb-3 block">{language === 'es' ? 'Métodos de comunicación' : 'Communication methods'} *</Label>
                <div className="space-y-3">
                  {[
                    { value: 'email', label: { es: 'Correo electrónico', en: 'Email' }, desc: { es: 'Enviar resumen de resultados por correo', en: 'Send results summary by email' } },
                    { value: 'reunion', label: { es: 'Reunión informativa', en: 'Informational meeting' }, desc: { es: 'Presentar resultados en junta con los trabajadores', en: 'Present results in a meeting with workers' } },
                    { value: 'intranet', label: { es: 'Intranet / Portal interno', en: 'Intranet / Internal portal' }, desc: { es: 'Publicar resultados en el sistema interno', en: 'Publish results on the internal system' } },
                    { value: 'cartel', label: { es: 'Cartel o infografía', en: 'Poster or infographic' }, desc: { es: 'Colocar resumen visual en áreas comunes', en: 'Place visual summary in common areas' } },
                    { value: 'capacitacion', label: { es: 'Capacitación', en: 'Training session' }, desc: { es: 'Incluir en sesiones de formación', en: 'Include in training sessions' } },
                  ].map((method) => (
                    <label 
                      key={method.value} 
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        formData.metodos_comunicacion?.includes(method.value) 
                          ? 'bg-teal-50 border-teal-300' 
                          : 'bg-white border-gray-200 hover:border-teal-200'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.metodos_comunicacion?.includes(method.value) || false}
                        onChange={(e) => {
                          const current = formData.metodos_comunicacion || [];
                          if (e.target.checked) {
                            setFormData({ ...formData, metodos_comunicacion: [...current, method.value] });
                          } else {
                            setFormData({ ...formData, metodos_comunicacion: current.filter(m => m !== method.value) });
                          }
                        }}
                        className="mt-1 w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                      />
                      <div>
                        <span className="font-medium text-gray-900">{method.label[language] || method.label.es}</span>
                        <p className="text-xs text-gray-500 mt-0.5">{method.desc[language] || method.desc.es}</p>
                      </div>
                    </label>
                  ))}
                  
                  {/* Opción "Otro" */}
                  <label 
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      formData.metodos_comunicacion?.includes('otro') 
                        ? 'bg-teal-50 border-teal-300' 
                        : 'bg-white border-gray-200 hover:border-teal-200'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.metodos_comunicacion?.includes('otro') || false}
                      onChange={(e) => {
                        const current = formData.metodos_comunicacion || [];
                        if (e.target.checked) {
                          setFormData({ ...formData, metodos_comunicacion: [...current, 'otro'] });
                        } else {
                          setFormData({ ...formData, metodos_comunicacion: current.filter(m => m !== 'otro'), metodo_comunicacion_otro: '' });
                        }
                      }}
                      className="mt-1 w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                    />
                    <div className="flex-1">
                      <span className="font-medium text-gray-900">{language === 'es' ? 'Otro método' : 'Other method'}</span>
                      <p className="text-xs text-gray-500 mt-0.5">{language === 'es' ? 'Especifica un método diferente' : 'Specify a different method'}</p>
                    </div>
                  </label>
                  
                  {formData.metodos_comunicacion?.includes('otro') && (
                    <div className="ml-7">
                      <Input
                        placeholder={language === 'es' ? 'Describe el método de comunicación...' : 'Describe the communication method...'}
                        value={formData.metodo_comunicacion_otro || ''}
                        onChange={(e) => setFormData({ ...formData, metodo_comunicacion_otro: e.target.value })}
                        className="mt-2"
                      />
                    </div>
                  )}
                </div>
                
                {(!formData.metodos_comunicacion || formData.metodos_comunicacion.length === 0) && (
                  <p className="text-sm text-amber-600 mt-2">
                    {language === 'es' ? 'Selecciona al menos un método de comunicación' : 'Select at least one communication method'}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between">
          <Button variant="outline" disabled={currentStep === 0} onClick={() => setCurrentStep(currentStep - 1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'es' ? 'Atrás' : 'Back'}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {language === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button onClick={handleSaveStep} disabled={saving}>
              {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
              {currentStep === STEP_ORDER.length - 1 
                ? (language === 'es' ? 'Completar' : 'Complete')
                : (language === 'es' ? 'Guardar y continuar' : 'Save and continue')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============== PROGRAMA DE ATENCIÓN DIALOG (M8-D) ==============
const ProgramaAtencionDialog = ({ open, onOpenChange, empresaId, onComplete }) => {
  const { language } = useLanguage();
  const [planes, setPlanes] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedTarea, setSelectedTarea] = useState(null);
  const [showTareaDetail, setShowTareaDetail] = useState(false);
  const [tareaDetail, setTareaDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [updatingTarea, setUpdatingTarea] = useState(false);
  const [nuevoComentario, setNuevoComentario] = useState('');
  const [nuevoProgreso, setNuevoProgreso] = useState(0);
  const [nuevoEstado, setNuevoEstado] = useState('');

  useEffect(() => {
    if (open) loadPrograma();
  }, [open]);

  const loadPrograma = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/v5/nom035/${empresaId}/programa-atencion?lang=${language}`);
      if (res.data.success) {
        setPlanes(res.data.data.planes || []);
        setStats(res.data.data.stats || {});
      }
    } catch (error) {
      toast.error('Error loading program');
    } finally {
      setLoading(false);
    }
  };

  const loadTareaDetail = async (tareaId) => {
    setLoadingDetail(true);
    try {
      const res = await axios.get(`/v5/nom035/${empresaId}/programa-atencion/tarea/${tareaId}?lang=${language}`);
      if (res.data.success) {
        setTareaDetail(res.data.data);
        setNuevoProgreso(res.data.data.progreso || 0);
        setNuevoEstado(res.data.data.estado || 'PENDIENTE');
        setShowTareaDetail(true);
      }
    } catch (error) {
      toast.error('Error loading task details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleUpdateTarea = async () => {
    if (!tareaDetail) return;
    setUpdatingTarea(true);
    try {
      const res = await axios.put(`/v5/nom035/${empresaId}/programa-atencion/tarea/${tareaDetail.id}`, {
        estado: nuevoEstado,
        progreso: nuevoProgreso,
        comentario: nuevoComentario.trim() || null
      });
      if (res.data.success) {
        toast.success(language === 'es' ? 'Tarea actualizada' : 'Task updated');
        setNuevoComentario('');
        loadTareaDetail(tareaDetail.id);
        loadPrograma();
        onComplete?.();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating task');
    } finally {
      setUpdatingTarea(false);
    }
  };

  const handleUploadEvidencia = async (file) => {
    if (!tareaDetail || !file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(
        `/v5/nom035/${empresaId}/programa-atencion/tarea/${tareaDetail.id}/evidencia`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (res.data.success) {
        toast.success(language === 'es' ? 'Evidencia subida' : 'Evidence uploaded');
        loadTareaDetail(tareaDetail.id);
        loadPrograma();
        onComplete?.();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error uploading evidence');
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`/v5/nom035/${empresaId}/programa-atencion/generate`);
      if (res.data.success) {
        toast.success(res.data.message);
        loadPrograma();
        onComplete();
      }
    } catch (error) {
      toast.error('Error generating program');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadTemplate = async (templateKey) => {
    try {
      const res = await axios.get(`/v5/nom035/${empresaId}/templates/${templateKey}?lang=${language}`);
      if (res.data.success) {
        const blob = new Blob([res.data.data.contenido], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${templateKey}.txt`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(language === 'es' ? 'Template descargado' : 'Template downloaded');
      }
    } catch (error) {
      toast.error('Error downloading template');
    }
  };

  const prioridadColors = {
    URGENTE: 'bg-red-100 text-red-800 border-red-300',
    ALTA: 'bg-orange-100 text-orange-800 border-orange-300',
    MEDIA: 'bg-yellow-100 text-yellow-800 border-yellow-300'
  };

  const estadoColors = {
    PENDIENTE: 'bg-gray-100 text-gray-700',
    EN_PROGRESO: 'bg-blue-100 text-blue-700',
    EVIDENCIADA: 'bg-green-100 text-green-700',
    COMPLETADA: 'bg-emerald-100 text-emerald-700'
  };

  const estadoLabels = {
    PENDIENTE: language === 'es' ? 'Pendiente' : 'Pending',
    EN_PROGRESO: language === 'es' ? 'En Progreso' : 'In Progress',
    EVIDENCIADA: language === 'es' ? 'Evidenciada' : 'Evidenced',
    COMPLETADA: language === 'es' ? 'Completada' : 'Completed'
  };

  // Vista de detalle de tarea
  if (showTareaDetail && tareaDetail) {
    return (
      <Dialog open={open} onOpenChange={(o) => { if (!o) { setShowTareaDetail(false); setTareaDetail(null); } onOpenChange(o); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => { setShowTareaDetail(false); setTareaDetail(null); }}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <DialogTitle className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-teal-600" />
                {language === 'es' ? 'Detalle de Tarea' : 'Task Detail'}
              </DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            {/* Info de la tarea */}
            <div className="p-4 bg-gray-50 rounded-lg border">
              <h3 className="font-semibold text-lg">{tareaDetail.titulo}</h3>
              <p className="text-sm text-gray-600 mt-1">{tareaDetail.descripcion || tareaDetail.tipo_evidencia}</p>
              <div className="flex items-center gap-4 mt-3">
                <Badge className={estadoColors[tareaDetail.estado]}>{estadoLabels[tareaDetail.estado]}</Badge>
                <span className="text-xs text-gray-500">
                  {language === 'es' ? 'Dominio:' : 'Domain:'} {tareaDetail.dominio}
                </span>
                {tareaDetail.fecha_limite && (
                  <span className="text-xs text-gray-500">
                    {language === 'es' ? 'Fecha límite:' : 'Deadline:'} {new Date(tareaDetail.fecha_limite).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>

            {/* Progreso */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>{language === 'es' ? 'Progreso' : 'Progress'}</Label>
                <span className="text-sm font-semibold text-teal-600">{nuevoProgreso}%</span>
              </div>
              <Slider
                value={[nuevoProgreso]}
                onValueChange={(value) => setNuevoProgreso(value[0])}
                max={100}
                step={5}
                className="w-full [&_[role=slider]]:bg-teal-600 [&_[role=slider]]:border-teal-600 [&>span:first-child]:bg-gray-200 [&>span:first-child>span]:bg-teal-600"
              />
            </div>

            {/* Estado */}
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Estado' : 'Status'}</Label>
              <Select value={nuevoEstado} onValueChange={setNuevoEstado}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PENDIENTE">{estadoLabels.PENDIENTE}</SelectItem>
                  <SelectItem value="EN_PROGRESO">{estadoLabels.EN_PROGRESO}</SelectItem>
                  <SelectItem value="EVIDENCIADA">{estadoLabels.EVIDENCIADA}</SelectItem>
                  <SelectItem value="COMPLETADA">{estadoLabels.COMPLETADA}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Subir evidencia */}
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Subir Evidencia' : 'Upload Evidence'}</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls"
                  onChange={(e) => e.target.files?.[0] && handleUploadEvidencia(e.target.files[0])}
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-gray-500">
                {language === 'es' ? 'Formatos: PDF, Word, Excel, Imágenes' : 'Formats: PDF, Word, Excel, Images'}
              </p>
            </div>

            {/* Evidencias existentes */}
            {tareaDetail.evidencias && tareaDetail.evidencias.length > 0 && (
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Evidencias subidas' : 'Uploaded evidence'}</Label>
                <div className="space-y-1">
                  {tareaDetail.evidencias.map((ev, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-green-50 rounded border border-green-200">
                      <FileCheck className="w-4 h-4 text-green-600" />
                      <span className="text-sm">{ev.nombre_archivo || `Evidencia ${idx + 1}`}</span>
                      <span className="text-xs text-gray-500 ml-auto">
                        {new Date(ev.fecha_subida).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Añadir comentario */}
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Añadir Comentario' : 'Add Comment'}</Label>
              <Textarea
                value={nuevoComentario}
                onChange={(e) => setNuevoComentario(e.target.value)}
                placeholder={language === 'es' ? 'Escribe un comentario o nota de seguimiento...' : 'Write a comment or follow-up note...'}
                rows={3}
              />
            </div>

            {/* Historial de comentarios */}
            {tareaDetail.comentarios && tareaDetail.comentarios.length > 0 && (
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Historial de Comentarios' : 'Comment History'}</Label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {tareaDetail.comentarios.map((com, idx) => (
                    <div key={idx} className="p-2 bg-gray-50 rounded border text-sm">
                      <p>{com.texto}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {com.usuario || 'Admin'} - {new Date(com.fecha).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Botones */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => { setShowTareaDetail(false); setTareaDetail(null); }}>
                {language === 'es' ? 'Cancelar' : 'Cancel'}
              </Button>
              <Button onClick={handleUpdateTarea} disabled={updatingTarea} className="bg-teal-600 hover:bg-teal-700">
                {updatingTarea && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
                {language === 'es' ? 'Guardar Cambios' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Vista principal del programa
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-teal-600" />
            {language === 'es' ? 'Programa de Atención NOM-035' : 'NOM-035 Attention Program'}
          </DialogTitle>
          <DialogDescription>
            {language === 'es' 
              ? 'Planes de acción generados automáticamente según el nivel de riesgo detectado'
              : 'Action plans automatically generated based on detected risk level'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-4 gap-2 mb-4">
              <div className="text-center p-2 bg-gray-50 rounded">
                <span className="text-xl font-bold">{stats.total || 0}</span>
                <p className="text-xs text-gray-500">Total</p>
              </div>
              <div className="text-center p-2 bg-green-50 rounded">
                <span className="text-xl font-bold text-green-700">{stats.evidenciadas || 0}</span>
                <p className="text-xs text-gray-500">{language === 'es' ? 'Evidenciadas' : 'Evidenced'}</p>
              </div>
              <div className="text-center p-2 bg-yellow-50 rounded">
                <span className="text-xl font-bold text-yellow-700">{(stats.pendientes || 0) + (stats.en_progreso || 0)}</span>
                <p className="text-xs text-gray-500">{language === 'es' ? 'Pendientes' : 'Pending'}</p>
              </div>
              <div className="text-center p-2 bg-red-50 rounded">
                <span className="text-xl font-bold text-red-700">{stats.vencidas || 0}</span>
                <p className="text-xs text-gray-500">{language === 'es' ? 'Vencidas' : 'Overdue'}</p>
              </div>
            </div>

            {/* Generate button */}
            {planes.length === 0 ? (
              <div className="text-center py-6 border-2 border-dashed rounded-lg mb-4">
                <Activity className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                <p className="text-gray-500 mb-4">
                  {language === 'es' 
                    ? 'No hay planes generados. Genera el programa basado en los resultados de Guía III.'
                    : 'No plans generated. Generate program based on Guide III results.'}
                </p>
                <Button onClick={handleGenerate} disabled={generating}>
                  {generating && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
                  <Zap className="w-4 h-4 mr-2" />
                  {language === 'es' ? 'Generar Programa' : 'Generate Program'}
                </Button>
              </div>
            ) : (stats.total === 0 || planes.every(p => !p.tareas || p.tareas.length === 0)) && (
              <div className="text-center py-4 border-2 border-dashed border-yellow-300 bg-yellow-50 rounded-lg mb-4">
                <RefreshCw className="w-8 h-8 mx-auto text-yellow-600 mb-2" />
                <p className="text-yellow-700 mb-3">
                  {language === 'es' 
                    ? 'Los planes existen pero no tienen tareas. Regenera para crear las tareas.'
                    : 'Plans exist but have no tasks. Regenerate to create tasks.'}
                </p>
                <Button onClick={handleGenerate} disabled={generating} variant="outline" className="border-yellow-500 text-yellow-700 hover:bg-yellow-100">
                  {generating && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
                  <Zap className="w-4 h-4 mr-2" />
                  {language === 'es' ? 'Regenerar Tareas' : 'Regenerate Tasks'}
                </Button>
              </div>
            )}

            {/* Plans list */}
            <div className="space-y-4">
              {planes.map(plan => (
                <div key={plan.id} className={`border rounded-lg p-4 ${prioridadColors[plan.prioridad] || 'bg-gray-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <Badge>{plan.prioridad}</Badge>
                      <h4 className="font-medium mt-1">{plan.dominio}</h4>
                      <p className="text-xs text-gray-600">
                        {language === 'es' ? 'Capital MEVIA:' : 'MEVIA Capital:'} {plan.capital_mevia}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline">{plan.nivel_riesgo}</Badge>
                      <p className="text-xs text-gray-500 mt-1">
                        {plan.empleados_afectados} {language === 'es' ? 'empleados' : 'employees'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Tasks - clickable */}
                  <div className="space-y-2 mt-3">
                    {plan.tareas?.map(tarea => (
                      <div 
                        key={tarea.id} 
                        className="flex items-center justify-between p-3 bg-white rounded border hover:border-teal-400 hover:shadow-sm transition-all cursor-pointer"
                        onClick={() => loadTareaDetail(tarea.id)}
                        data-testid={`tarea-${tarea.id}`}
                      >
                        <div className="flex items-center gap-3 flex-1">
                          {tarea.estado === 'EVIDENCIADA' || tarea.estado === 'COMPLETADA' ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : tarea.estado === 'EN_PROGRESO' ? (
                            <Clock className="w-5 h-5 text-blue-500" />
                          ) : (
                            <Clock className="w-5 h-5 text-gray-400" />
                          )}
                          <div className="flex-1">
                            <p className="text-sm font-medium">{tarea.titulo}</p>
                            <p className="text-xs text-gray-500">{tarea.tipo_evidencia}</p>
                          </div>
                          <Badge className={`text-xs ${estadoColors[tarea.estado] || 'bg-gray-100'}`}>
                            {estadoLabels[tarea.estado] || tarea.estado}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          {tarea.tiene_template && (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={(e) => { e.stopPropagation(); handleDownloadTemplate(tarea.template_key); }}
                              title={language === 'es' ? 'Descargar plantilla' : 'Download template'}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          )}
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Tip */}
            {planes.length > 0 && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>💡 {language === 'es' ? 'Tip:' : 'Tip:'}</strong> {language === 'es' 
                    ? 'Haz clic en cualquier tarea para ver detalles, actualizar progreso, subir evidencias y añadir comentarios.'
                    : 'Click on any task to view details, update progress, upload evidence and add comments.'}
                </p>
              </div>
            )}
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {language === 'es' ? 'Cerrar' : 'Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============== EXPEDIENTE DIALOG (M8-D) ==============
const ExpedienteDialog = ({ open, onOpenChange, empresaId }) => {
  const { language } = useLanguage();
  const [expediente, setExpediente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (open) loadExpediente();
  }, [open]);

  const loadExpediente = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/v5/nom035/${empresaId}/expediente/preview?lang=${language}`);
      if (res.data.success) {
        setExpediente(res.data.data);
      }
    } catch (error) {
      toast.error('Error loading file');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const confirmar = expediente?.ipa_score < 70;
      
      // Descargar PDF directamente
      const response = await axios.post(
        `/v5/nom035/${empresaId}/expediente/pdf?confirmar_parcial=${confirmar}&lang=${language}`,
        {},
        { responseType: 'blob' }
      );
      
      // Crear enlace de descarga
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Expediente_NOM035_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(language === 'es' ? 'Expediente PDF descargado' : 'PDF file downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error exporting PDF');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-teal-600" />
            {language === 'es' ? 'Expediente de Cumplimiento NOM-035' : 'NOM-035 Compliance File'}
          </DialogTitle>
          <DialogDescription>
            {language === 'es' 
              ? '10 secciones requeridas para una auditoría de la STPS'
              : '10 sections required for an STPS audit'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <>
            {/* IPA indicator */}
            <div className={`p-4 rounded-lg mb-4 ${
              expediente?.ipa_score >= 90 ? 'bg-green-100 border-green-300' :
              expediente?.ipa_score >= 70 ? 'bg-yellow-100 border-yellow-300' :
              'bg-red-100 border-red-300'
            } border`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">
                    IPA: {expediente?.ipa_score}%
                  </p>
                  <p className="text-sm text-gray-600">
                    {expediente?.completas}/10 {language === 'es' ? 'secciones completas' : 'sections complete'}
                  </p>
                </div>
                {expediente?.listo_para_auditoria && (
                  <Badge className="bg-green-500">
                    {language === 'es' ? 'Listo para auditoría' : 'Audit ready'}
                  </Badge>
                )}
              </div>
            </div>

            {/* Warning if incomplete */}
            {expediente?.advertencia && (
              <div className="p-3 bg-orange-100 border border-orange-300 rounded-lg mb-4">
                <div className="flex items-center gap-2 text-orange-800">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-sm">
                    {language === 'es' ? expediente.advertencia.mensaje_es : expediente.advertencia.mensaje_en}
                  </span>
                </div>
              </div>
            )}

            {/* 10 Sections */}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {expediente?.secciones?.map((seccion, idx) => (
                <div 
                  key={idx}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${
                    seccion.completa 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    seccion.completa ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
                  }`}>
                    {seccion.completa ? <Check className="w-4 h-4" /> : seccion.num}
                  </div>
                  <span className="text-sm flex-1">
                    {language === 'es' ? seccion.nombre_es : seccion.nombre_en}
                  </span>
                  {seccion.completa && <CheckCircle className="w-4 h-4 text-green-500" />}
                </div>
              ))}
            </div>
          </>
        )}

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {language === 'es' ? 'Cerrar' : 'Close'}
          </Button>
          <Button onClick={handleExport} disabled={exporting}>
            {exporting && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
            <Download className="w-4 h-4 mr-2" />
            {language === 'es' ? 'Exportar Expediente' : 'Export File'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ============== REPORTES NOM-035 (M8-J) ==============
const ReportsDialog = ({ open, onOpenChange, empresaId }) => {
  const { language } = useLanguage();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    if (open) loadReports();
  }, [open]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/v5/nom035/reports/${empresaId}/available?lang=${language}`);
      if (res.data.success) {
        setReports(res.data.data.reports || []);
      }
    } catch (error) {
      toast.error('Error loading reports');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (report) => {
    if (!report.disponible) {
      toast.error(language === 'es' ? 'Reporte no disponible aún' : 'Report not available yet');
      return;
    }
    
    setDownloading(report.id);
    try {
      const isCSV = report.formato === 'csv';
      
      // R01 (Expediente) requiere POST, los demás usan GET
      let response;
      if (report.id === 'R01') {
        response = await axios.post(report.endpoint, { confirmar_parcial: false }, { responseType: 'blob' });
      } else {
        response = await axios.get(report.endpoint, { responseType: 'blob' });
      }
      
      const mimeType = isCSV ? 'text/csv' : 'application/pdf';
      const extension = isCSV ? 'csv' : 'pdf';
      
      const blob = new Blob([response.data], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.id}_${new Date().toISOString().split('T')[0]}.${extension}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(language === 'es' ? 'Reporte descargado' : 'Report downloaded');
    } catch (error) {
      logger.error('Download error:', error);
      toast.error(error.response?.data?.detail || 'Error downloading report');
    } finally {
      setDownloading(null);
    }
  };

  const getReportIcon = (id) => {
    const icons = {
      'R01': FolderOpen,
      'R02': AlertCircle,
      'R03': BarChart3,
      'R04': ClipboardList,
      'R05': FileCheck,
      'R06': MessageSquare,
      'R07': LayoutGrid,
      'R08': Users,
      'R09': Activity,
      'R10': Calendar,
      'R11': Shield,
      'R17': FileText,
      'R18': Megaphone,
      'R12': TrendingUp,
      'R13': PieChart,
      'R14': Activity
    };
    return icons[id] || FileText;
  };

  const getGrupoInfo = (grupo) => {
    const grupos = {
      1: { 
        label_es: 'Obligatorios STPS', 
        label_en: 'STPS Mandatory',
        color: 'bg-green-50 border-green-200',
        badgeColor: 'bg-green-100 text-green-700'
      },
      2: { 
        label_es: 'Gestión Interna', 
        label_en: 'Internal Management',
        color: 'bg-blue-50 border-blue-200',
        badgeColor: 'bg-blue-100 text-blue-700'
      },
      3: { 
        label_es: 'Diferenciadores MEVIA', 
        label_en: 'MEVIA Differentiators',
        color: 'bg-indigo-50 border-indigo-200',
        badgeColor: 'bg-indigo-100 text-indigo-700'
      },
      5: { 
        label_es: 'Exportación de Datos', 
        label_en: 'Data Export',
        color: 'bg-amber-50 border-amber-200',
        badgeColor: 'bg-amber-100 text-amber-700'
      },
      6: { 
        label_es: 'Comunicación', 
        label_en: 'Communication',
        color: 'bg-purple-50 border-purple-200',
        badgeColor: 'bg-purple-100 text-purple-700'
      }
    };
    return grupos[grupo] || grupos[1];
  };

  // Agrupar reportes por grupo
  const reportsByGroup = reports.reduce((acc, report) => {
    const grupo = report.grupo || 1;
    if (!acc[grupo]) acc[grupo] = [];
    acc[grupo].push(report);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            {language === 'es' ? 'Reportes NOM-035' : 'NOM-035 Reports'}
          </DialogTitle>
          <DialogDescription>
            {language === 'es' 
              ? 'Reportes obligatorios y de gestión. Exporta a PDF.'
              : 'Mandatory and management reports. Export to PDF.'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(reportsByGroup).map(([grupo, groupReports]) => {
              const grupoInfo = getGrupoInfo(parseInt(grupo));
              return (
                <div key={grupo} className={`rounded-lg border p-4 ${grupoInfo.color}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Badge className={grupoInfo.badgeColor}>
                      {language === 'es' ? grupoInfo.label_es : grupoInfo.label_en}
                    </Badge>
                    <span className="text-xs text-gray-500">
                      ({groupReports.length} {language === 'es' ? 'reportes' : 'reports'})
                    </span>
                  </div>
                  
                  <div className="space-y-2">
                    {groupReports.map((report) => {
                      const IconComponent = getReportIcon(report.id);
                      const isConfidential = report.prioridad === 'confidencial';
                      
                      return (
                        <div 
                          key={report.id}
                          className={`flex items-center justify-between p-3 rounded-lg border bg-white ${
                            report.disponible 
                              ? 'border-gray-200 hover:border-blue-300' 
                              : 'border-gray-200 opacity-60'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                              isConfidential 
                                ? 'bg-purple-100 text-purple-600'
                                : report.disponible 
                                  ? 'bg-blue-100 text-blue-600' 
                                  : 'bg-gray-200 text-gray-400'
                            }`}>
                              <IconComponent className="w-4 h-4" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-mono text-gray-400">{report.id}</span>
                                <span className="font-medium text-sm">
                                  {language === 'es' ? report.nombre_es : report.nombre_en}
                                </span>
                                {isConfidential && (
                                  <Badge variant="outline" className="text-xs text-purple-600 border-purple-300">
                                    <Lock className="w-3 h-3 mr-1" />
                                    {language === 'es' ? 'Confidencial' : 'Confidential'}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-gray-500">
                                {report.disponible 
                                  ? (language === 'es' ? 'Disponible' : 'Available')
                                  : (language === 'es' ? 'Requiere datos' : 'Requires data')
                                }
                              </p>
                            </div>
                          </div>
                          
                          <Button
                            size="sm"
                            variant={report.disponible ? "default" : "outline"}
                            disabled={!report.disponible || downloading === report.id}
                            onClick={() => handleDownload(report)}
                            className={report.disponible 
                              ? isConfidential 
                                ? 'bg-purple-600 hover:bg-purple-700' 
                                : 'bg-blue-600 hover:bg-blue-700' 
                              : ''
                            }
                          >
                            {downloading === report.id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Download className="w-4 h-4 mr-1" />
                                {report.formato === 'csv' ? 'CSV' : 'PDF'}
                              </>
                            )}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            
            {reports.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                {language === 'es' 
                  ? 'No hay reportes disponibles. Completa la configuración NOM-035.'
                  : 'No reports available. Complete NOM-035 configuration.'}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {language === 'es' ? 'Cerrar' : 'Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ============== BUZÓN ANÓNIMO - EMPLEADO (M8-E-REV v2) ==============
const BuzonEmpleadoDialog = ({ open, onOpenChange, empresaId }) => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('enviar'); // 'enviar', 'mis-mensajes', 'consulta'
  const [step, setStep] = useState('form'); // 'form', 'confirmacion'
  const [categorias, setCategorias] = useState([]);
  const [categoriasCanalizacion, setCategoriasCanalizacion] = useState([]);
  const [responsableNombre, setResponsableNombre] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [categoria, setCategoria] = useState('');
  const [esAnonimo, setEsAnonimo] = useState(false);
  const [aceptaConsentimiento, setAceptaConsentimiento] = useState(false);
  const [codigoSeguimiento, setCodigoSeguimiento] = useState('');
  const [mensajeEsAnonimo, setMensajeEsAnonimo] = useState(false);
  // Consulta
  const [codigoConsulta, setCodigoConsulta] = useState('');
  const [resultadoConsulta, setResultadoConsulta] = useState(null);
  const [consulting, setConsulting] = useState(false);
  // Mis mensajes
  const [misMensajes, setMisMensajes] = useState([]);
  const [loadingMensajes, setLoadingMensajes] = useState(false);

  // Determina si la categoría requiere canalización (NO permite anonimato)
  const categoriaRequiereCanalizacion = categoriasCanalizacion.includes(categoria);

  useEffect(() => {
    if (open) {
      loadConsentimiento();
      setStep('form');
      setMensaje('');
      setCategoria('');
      setEsAnonimo(false);
      setAceptaConsentimiento(false);
      setCodigoSeguimiento('');
      setCodigoConsulta('');
      setResultadoConsulta(null);
    }
  }, [open]);

  useEffect(() => {
    if (open && activeTab === 'mis-mensajes') {
      loadMisMensajes();
    }
  }, [activeTab, open]);

  const loadConsentimiento = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/v5/nom035/${empresaId}/buzon/consentimiento?lang=${language}`);
      if (res.data.success) {
        setCategorias(res.data.data.categorias || []);
        setCategoriasCanalizacion(res.data.data.categorias_canalizacion_obligatoria || []);
        setResponsableNombre(res.data.data.responsable_nombre || '');
      }
    } catch (error) {
      toast.error(language === 'es' ? 'Error cargando información' : 'Error loading information');
    } finally {
      setLoading(false);
    }
  };

  const loadMisMensajes = async () => {
    if (!user?.id) return;
    setLoadingMensajes(true);
    try {
      const res = await axios.get(`/v5/nom035/${empresaId}/buzon/mis-mensajes/${user.id}?lang=${language}`);
      if (res.data.success) {
        setMisMensajes(res.data.data.mensajes || []);
      }
    } catch (error) {
      logger.error('Error loading mis mensajes:', error);
    } finally {
      setLoadingMensajes(false);
    }
  };

  const handleEnviar = async () => {
    if (!categoria) {
      toast.error(language === 'es' ? 'Selecciona una categoría' : 'Select a category');
      return;
    }
    if (!mensaje.trim() || mensaje.trim().length < 10) {
      toast.error(language === 'es' ? 'El mensaje debe tener al menos 10 caracteres' : 'Message must have at least 10 characters');
      return;
    }
    if (!aceptaConsentimiento) {
      toast.error(language === 'es' ? 'Debes aceptar los términos' : 'You must accept the terms');
      return;
    }
    
    // Determinar si será anónimo (solo si no requiere canalización Y eligió anónimo)
    const seraAnonimo = esAnonimo && !categoriaRequiereCanalizacion;
    
    setSending(true);
    try {
      const res = await axios.post(`/v5/nom035/${empresaId}/buzon/enviar?user_id=${user?.id || ''}`, {
        contenido: mensaje,
        categoria: categoria,
        es_anonimo: seraAnonimo,
        acepta_consentimiento: true
      });
      
      if (res.data.success) {
        setCodigoSeguimiento(res.data.data.codigo_seguimiento);
        setMensajeEsAnonimo(res.data.data.es_anonimo);
        setStep('confirmacion');
        toast.success(res.data.data.es_anonimo 
          ? (language === 'es' ? 'Mensaje enviado de forma anónima' : 'Message sent anonymously')
          : (language === 'es' ? 'Mensaje enviado de forma confidencial' : 'Message sent confidentially')
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Error al enviar' : 'Error sending'));
    } finally {
      setSending(false);
    }
  };

  const handleConsultar = async () => {
    if (!codigoConsulta.trim() || codigoConsulta.length !== 6) {
      toast.error(language === 'es' ? 'Ingresa un código de 6 dígitos' : 'Enter a 6-digit code');
      return;
    }
    
    setConsulting(true);
    setResultadoConsulta(null);
    try {
      const res = await axios.get(`/v5/nom035/buzon/consultar/${codigoConsulta.toUpperCase()}?lang=${language}`);
      if (res.data.success) {
        setResultadoConsulta(res.data.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Código no encontrado' : 'Code not found'));
    } finally {
      setConsulting(false);
    }
  };

  const estadoColors = {
    NUEVO: 'bg-blue-100 text-blue-800',
    EN_REVISION: 'bg-yellow-100 text-yellow-800',
    ATENDIDO: 'bg-green-100 text-green-800'
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-purple-600" />
            {language === 'es' ? 'Buzón de Comunicación' : 'Communication Mailbox'}
          </DialogTitle>
          <DialogDescription>
            {language === 'es' 
              ? 'Canal seguro para reportar situaciones de riesgo psicosocial'
              : 'Safe channel to report psychosocial risk situations'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-purple-500" />
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="enviar">
                <Send className="w-4 h-4 mr-2" />
                {language === 'es' ? 'Enviar' : 'Send'}
              </TabsTrigger>
              <TabsTrigger value="mis-mensajes">
                <Inbox className="w-4 h-4 mr-2" />
                {language === 'es' ? 'Mis Mensajes' : 'My Messages'}
              </TabsTrigger>
              <TabsTrigger value="consulta">
                <Search className="w-4 h-4 mr-2" />
                {language === 'es' ? 'Consultar' : 'Check Status'}
              </TabsTrigger>
            </TabsList>

            {/* TAB: ENVIAR MENSAJE */}
            <TabsContent value="enviar" className="space-y-4 mt-4">
              {step === 'form' ? (
                <>
                  {/* Info del responsable */}
                  {responsableNombre && (
                    <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <p className="text-sm text-purple-700">
                        <span className="font-medium">{language === 'es' ? 'Responsable NOM-035: ' : 'NOM-035 Responsible: '}</span>
                        {responsableNombre}
                      </p>
                    </div>
                  )}

                  {/* Selector de categoría */}
                  <div className="space-y-2">
                    <Label>{language === 'es' ? 'Categoría del reporte *' : 'Report category *'}</Label>
                    <Select value={categoria} onValueChange={(v) => { setCategoria(v); setEsAnonimo(false); }}>
                      <SelectTrigger>
                        <SelectValue placeholder={language === 'es' ? 'Selecciona una categoría' : 'Select a category'} />
                      </SelectTrigger>
                      <SelectContent>
                        {categorias.map(cat => (
                          <SelectItem key={cat.key} value={cat.key}>
                            {cat.nombre}
                            {cat.requiere_canalizacion && ' ⚠️'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Aviso dinámico según categoría */}
                  {categoria && (
                    <div className={`p-3 rounded-lg border ${categoriaRequiereCanalizacion ? 'bg-amber-50 border-amber-300' : 'bg-blue-50 border-blue-200'}`}>
                      {categoriaRequiereCanalizacion ? (
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-amber-800">
                            <p className="font-medium">
                              {language === 'es' ? 'Canalización obligatoria' : 'Mandatory channeling'}
                            </p>
                            <p className="mt-1">
                              {language === 'es' 
                                ? 'Por la naturaleza de este reporte, tu identidad será compartida únicamente con el Responsable NOM-035 para dar seguimiento adecuado. Tu información será tratada de manera confidencial.'
                                : 'Due to the nature of this report, your identity will be shared only with the NOM-035 Responsible for proper follow-up. Your information will be treated confidentially.'}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-sm text-blue-800">
                            {language === 'es' 
                              ? 'Puedes elegir si deseas enviar este mensaje de forma anónima o confidencial.'
                              : 'You can choose whether to send this message anonymously or confidentially.'}
                          </p>
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              id="anonimo-checkbox"
                              checked={esAnonimo}
                              onChange={(e) => setEsAnonimo(e.target.checked)}
                              className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                            />
                            <label htmlFor="anonimo-checkbox" className="text-sm text-gray-700">
                              {language === 'es' 
                                ? 'Enviar de forma anónima (tu identidad NO será visible)'
                                : 'Send anonymously (your identity will NOT be visible)'}
                            </label>
                          </div>
                          <p className="text-xs text-gray-500">
                            {esAnonimo
                              ? (language === 'es' 
                                  ? 'Mensaje ANÓNIMO: Nadie podrá identificarte. Usa el código de seguimiento para ver respuestas.'
                                  : 'ANONYMOUS message: No one will be able to identify you. Use the tracking code to see responses.')
                              : (language === 'es'
                                  ? 'Mensaje CONFIDENCIAL: Solo el Responsable NOM-035 verá tu identidad.'
                                  : 'CONFIDENTIAL message: Only the NOM-035 Responsible will see your identity.')
                            }
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Textarea del mensaje */}
                  <div className="space-y-2">
                    <Label>{language === 'es' ? 'Describe la situación *' : 'Describe the situation *'}</Label>
                    <Textarea
                      value={mensaje}
                      onChange={(e) => setMensaje(e.target.value)}
                      placeholder={language === 'es' 
                        ? 'Describe la situación con el mayor detalle posible...'
                        : 'Describe the situation in as much detail as possible...'}
                      className="min-h-[120px]"
                      maxLength={1500}
                    />
                    <p className="text-xs text-gray-500 text-right">{mensaje.length}/1500</p>
                  </div>

                  {/* Consentimiento */}
                  <div className="p-3 bg-gray-50 rounded-lg border">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="consentimiento"
                        checked={aceptaConsentimiento}
                        onChange={(e) => setAceptaConsentimiento(e.target.checked)}
                        className="mt-1 w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                      />
                      <label htmlFor="consentimiento" className="text-sm text-gray-700">
                        {language === 'es'
                          ? 'Confirmo que la información proporcionada es verídica y acepto que sea tratada conforme a la política de prevención de riesgos psicosociales de la empresa.'
                          : 'I confirm that the information provided is true and I accept that it will be treated in accordance with the company\'s psychosocial risk prevention policy.'}
                      </label>
                    </div>
                  </div>

                  <Button 
                    onClick={handleEnviar} 
                    disabled={sending || !categoria || !mensaje.trim() || !aceptaConsentimiento}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    {sending ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    {language === 'es' ? 'Enviar mensaje' : 'Send message'}
                  </Button>
                </>
              ) : (
                /* CONFIRMACIÓN */
                <div className="text-center space-y-4 py-4">
                  <div className={`w-16 h-16 rounded-full ${mensajeEsAnonimo ? 'bg-purple-100' : 'bg-green-100'} flex items-center justify-center mx-auto`}>
                    {mensajeEsAnonimo 
                      ? <User className="w-8 h-8 text-purple-600" />
                      : <Shield className="w-8 h-8 text-green-600" />
                    }
                  </div>
                  <h3 className="text-lg font-semibold">
                    {language === 'es' ? '¡Mensaje enviado!' : 'Message sent!'}
                  </h3>
                  <Badge className={mensajeEsAnonimo ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'}>
                    {mensajeEsAnonimo 
                      ? (language === 'es' ? 'Anónimo' : 'Anonymous')
                      : (language === 'es' ? 'Confidencial' : 'Confidential')
                    }
                  </Badge>
                  
                  <div className="p-4 bg-gray-100 rounded-lg">
                    <p className="text-sm text-gray-600 mb-2">
                      {language === 'es' ? 'Tu código de seguimiento:' : 'Your tracking code:'}
                    </p>
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-2xl font-mono font-bold tracking-wider">{codigoSeguimiento}</span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(codigoSeguimiento);
                          toast.success(language === 'es' ? 'Código copiado' : 'Code copied');
                        }}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600">
                    {mensajeEsAnonimo
                      ? (language === 'es' 
                          ? 'Guarda este código para consultar el estado de tu mensaje.'
                          : 'Save this code to check your message status.')
                      : (language === 'es'
                          ? 'También puedes ver el estado en la pestaña "Mis Mensajes".'
                          : 'You can also check the status in the "My Messages" tab.')
                    }
                  </p>

                  <Button variant="outline" onClick={() => { setStep('form'); setMensaje(''); setCategoria(''); setAceptaConsentimiento(false); setEsAnonimo(false); }}>
                    {language === 'es' ? 'Enviar otro mensaje' : 'Send another message'}
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* TAB: MIS MENSAJES */}
            <TabsContent value="mis-mensajes" className="space-y-4 mt-4">
              {loadingMensajes ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-purple-500" />
                </div>
              ) : misMensajes.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Inbox className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>{language === 'es' ? 'No tienes mensajes confidenciales enviados' : 'You have no confidential messages sent'}</p>
                  <p className="text-sm mt-1">
                    {language === 'es' 
                      ? 'Los mensajes anónimos solo pueden consultarse con código de seguimiento'
                      : 'Anonymous messages can only be checked with tracking code'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {misMensajes.map(msg => (
                    <div key={msg.id} className="p-4 bg-white rounded-lg border shadow-sm">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline">{msg.categoria_nombre}</Badge>
                            <Badge className={estadoColors[msg.estado]}>{msg.estado_display}</Badge>
                          </div>
                          <p className="text-sm text-gray-700">{msg.fragmento}</p>
                          <p className="text-xs text-gray-400 mt-2">
                            {new Date(msg.fecha).toLocaleDateString()} • {language === 'es' ? 'Código:' : 'Code:'} {msg.codigo_seguimiento}
                          </p>
                        </div>
                      </div>
                      {msg.tiene_respuesta && (
                        <div className="mt-3 p-3 bg-purple-50 rounded border border-purple-200">
                          <p className="text-xs text-purple-600 font-medium mb-1">
                            {language === 'es' ? 'Respuesta:' : 'Response:'}
                          </p>
                          <p className="text-sm text-gray-700">{msg.respuesta}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* TAB: CONSULTAR CON CÓDIGO */}
            <TabsContent value="consulta" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Código de seguimiento' : 'Tracking code'}</Label>
                <div className="flex gap-2">
                  <Input
                    value={codigoConsulta}
                    onChange={(e) => setCodigoConsulta(e.target.value.toUpperCase())}
                    placeholder="ABC123"
                    maxLength={6}
                    className="font-mono text-lg tracking-wider"
                  />
                  <Button onClick={handleConsultar} disabled={consulting}>
                    {consulting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {resultadoConsulta && (
                <div className="p-4 bg-gray-50 rounded-lg border space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">{language === 'es' ? 'Estado:' : 'Status:'}</span>
                    <Badge className={estadoColors[resultadoConsulta.estado]}>
                      {resultadoConsulta.estado_display}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">{language === 'es' ? 'Categoría:' : 'Category:'}</span>
                    <span className="text-sm">{resultadoConsulta.categoria_display || '-'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">{language === 'es' ? 'Fecha envío:' : 'Sent date:'}</span>
                    <span className="text-sm">{new Date(resultadoConsulta.fecha_envio).toLocaleDateString()}</span>
                  </div>
                  
                  {resultadoConsulta.respuesta_publica && (
                    <div className="mt-3 p-3 bg-white rounded border border-purple-200">
                      <p className="text-xs text-purple-600 font-medium mb-1">
                        {language === 'es' ? 'Respuesta del responsable:' : 'Responsible\'s response:'}
                      </p>
                      <p className="text-sm">{resultadoConsulta.respuesta_publica}</p>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ============== BUZÓN ANÓNIMO - ADMIN (M8-E) ==============
const BuzonAdminDialog = ({ open, onOpenChange, empresaId, onUpdate }) => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [mensajes, setMensajes] = useState([]);
  const [stats, setStats] = useState({ total: 0, nuevos: 0, en_revision: 0, atendidos: 0 });
  const [categorias, setCategorias] = useState([]);
  const [filtroCategoria, setFiltroCategoria] = useState('_all');
  const [filtroEstado, setFiltroEstado] = useState('_all');
  const [selectedMensaje, setSelectedMensaje] = useState(null);
  const [respuestaPublica, setRespuestaPublica] = useState('');
  const [notasInternas, setNotasInternas] = useState('');
  const [updating, setUpdating] = useState(false);
  const [tituloAccion, setTituloAccion] = useState('');
  const [showCrearAccion, setShowCrearAccion] = useState(false);

  useEffect(() => {
    if (open) {
      loadBandeja();
    }
  }, [open, filtroCategoria, filtroEstado]);

  const loadBandeja = async () => {
    setLoading(true);
    try {
      let url = `/v5/nom035/${empresaId}/buzon/admin/bandeja?lang=${language}`;
      if (filtroCategoria && filtroCategoria !== '_all') url += `&categoria=${filtroCategoria}`;
      if (filtroEstado && filtroEstado !== '_all') url += `&estado=${filtroEstado}`;
      
      const res = await axios.get(url);
      if (res.data.success) {
        setMensajes(res.data.data.mensajes || []);
        setStats(res.data.data.stats || {});
        setCategorias(res.data.data.categorias || []);
      }
    } catch (error) {
      toast.error(language === 'es' ? 'Error cargando mensajes' : 'Error loading messages');
    } finally {
      setLoading(false);
    }
  };

  const loadDetalle = async (mensajeId) => {
    try {
      const res = await axios.get(`/v5/nom035/${empresaId}/buzon/admin/mensaje/${mensajeId}?lang=${language}`);
      if (res.data.success) {
        setSelectedMensaje(res.data.data);
        setRespuestaPublica(res.data.data.respuesta_publica || '');
        setNotasInternas(res.data.data.notas_internas || '');
      }
    } catch (error) {
      toast.error(language === 'es' ? 'Error cargando detalle' : 'Error loading detail');
    }
  };

  const handleUpdateMensaje = async (nuevoEstado) => {
    if (!selectedMensaje) return;
    
    setUpdating(true);
    try {
      await axios.put(`/v5/nom035/${empresaId}/buzon/admin/mensaje/${selectedMensaje.id}`, {
        estado: nuevoEstado,
        respuesta_publica: respuestaPublica || null,
        notas_internas: notasInternas || null
      });
      
      toast.success(language === 'es' ? 'Mensaje actualizado' : 'Message updated');
      loadBandeja();
      onUpdate?.();
      
      // Recargar el mensaje actualizado
      loadDetalle(selectedMensaje.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Error al actualizar' : 'Error updating'));
    } finally {
      setUpdating(false);
    }
  };

  const handleCrearAccion = async () => {
    if (!selectedMensaje || !tituloAccion.trim()) return;
    
    setUpdating(true);
    try {
      await axios.post(
        `/v5/nom035/${empresaId}/buzon/admin/mensaje/${selectedMensaje.id}/generar-accion?titulo_accion=${encodeURIComponent(tituloAccion)}`
      );
      
      toast.success(language === 'es' ? 'Acción creada en Programa de Atención' : 'Action created in Attention Program');
      setShowCrearAccion(false);
      setTituloAccion('');
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Error al crear acción' : 'Error creating action'));
    } finally {
      setUpdating(false);
    }
  };

  const estadoColors = {
    'NUEVO': 'bg-yellow-100 text-yellow-800 border-yellow-300',
    'EN_REVISION': 'bg-blue-100 text-blue-800 border-blue-300',
    'ATENDIDO': 'bg-green-100 text-green-800 border-green-300'
  };

  const estadoLabels = {
    'NUEVO': language === 'es' ? 'Nuevo' : 'New',
    'EN_REVISION': language === 'es' ? 'En revisión' : 'In review',
    'ATENDIDO': language === 'es' ? 'Atendido' : 'Attended'
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Inbox className="w-5 h-5 text-purple-600" />
            {language === 'es' ? 'Bandeja de Mensajes Anónimos' : 'Anonymous Messages Inbox'}
          </DialogTitle>
          <DialogDescription className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
              {stats.nuevos || 0} {language === 'es' ? 'nuevos' : 'new'}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
              {stats.en_revision || 0} {language === 'es' ? 'en revisión' : 'in review'}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              {stats.atendidos || 0} {language === 'es' ? 'atendidos' : 'attended'}
            </span>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-5 gap-4 min-h-[400px]">
            {/* Lista de mensajes */}
            <div className="col-span-2 border rounded-lg overflow-hidden">
              {/* Filtros */}
              <div className="p-2 bg-gray-50 border-b flex gap-2">
                <Select value={filtroEstado} onValueChange={setFiltroEstado}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder={language === 'es' ? 'Estado' : 'Status'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">{language === 'es' ? 'Todos' : 'All'}</SelectItem>
                    <SelectItem value="NUEVO">{language === 'es' ? 'Nuevo' : 'New'}</SelectItem>
                    <SelectItem value="EN_REVISION">{language === 'es' ? 'En revisión' : 'In review'}</SelectItem>
                    <SelectItem value="ATENDIDO">{language === 'es' ? 'Atendido' : 'Attended'}</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filtroCategoria} onValueChange={setFiltroCategoria}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder={language === 'es' ? 'Categoría' : 'Category'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">{language === 'es' ? 'Todas' : 'All'}</SelectItem>
                    {categorias.map(cat => (
                      <SelectItem key={cat.key} value={cat.key}>{cat.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Lista */}
              <div className="overflow-y-auto max-h-[350px]">
                {mensajes.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    <Inbox className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{language === 'es' ? 'No hay mensajes' : 'No messages'}</p>
                  </div>
                ) : (
                  mensajes.map(msg => (
                    <div 
                      key={msg.id}
                      className={`p-3 border-b cursor-pointer hover:bg-gray-50 ${
                        selectedMensaje?.id === msg.id ? 'bg-purple-50' : ''
                      }`}
                      onClick={() => loadDetalle(msg.id)}
                      data-testid={`mensaje-${msg.id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1">
                          <Badge className={`text-xs ${estadoColors[msg.estado]}`}>
                            {estadoLabels[msg.estado]}
                          </Badge>
                          <Badge className={`text-xs ${msg.es_anonimo ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-700'}`}>
                            {msg.es_anonimo ? (language === 'es' ? 'Anónimo' : 'Anonymous') : (language === 'es' ? 'Confidencial' : 'Confidential')}
                          </Badge>
                        </div>
                        <span className="text-xs text-gray-400">
                          {new Date(msg.fecha).toLocaleDateString()}
                        </span>
                      </div>
                      {!msg.es_anonimo && msg.remitente && (
                        <p className="text-xs text-purple-600 mb-1 flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {msg.remitente.nombre}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mb-1">{msg.categoria_nombre || '-'}</p>
                      <p className="text-sm text-gray-700 line-clamp-2">{msg.fragmento}</p>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Detalle del mensaje */}
            <div className="col-span-3 border rounded-lg p-4">
              {selectedMensaje ? (
                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge className={estadoColors[selectedMensaje.estado]}>
                          {estadoLabels[selectedMensaje.estado]}
                        </Badge>
                        <Badge className={`${selectedMensaje.es_anonimo ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-700'}`}>
                          {selectedMensaje.es_anonimo ? (language === 'es' ? 'Anónimo' : 'Anonymous') : (language === 'es' ? 'Confidencial' : 'Confidential')}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500">
                        {new Date(selectedMensaje.fecha_recepcion).toLocaleString()}
                      </p>
                    </div>
                    <Badge variant="outline">{selectedMensaje.categoria_nombre || '-'}</Badge>
                  </div>

                  {/* Info del remitente si es confidencial */}
                  {!selectedMensaje.es_anonimo && selectedMensaje.remitente && (
                    <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <p className="text-xs text-purple-600 font-medium mb-1">
                        {language === 'es' ? 'Remitente identificado' : 'Identified sender'}
                      </p>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-purple-200 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-purple-700" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800">{selectedMensaje.remitente.nombre}</p>
                          <p className="text-xs text-gray-500">{selectedMensaje.remitente.email}</p>
                          {selectedMensaje.remitente.departamento && (
                            <p className="text-xs text-gray-400">{selectedMensaje.remitente.departamento}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Contenido */}
                  <div className="p-3 bg-gray-50 rounded-lg border max-h-32 overflow-y-auto">
                    <p className="text-sm whitespace-pre-wrap">{selectedMensaje.contenido}</p>
                  </div>

                  {/* Respuesta pública */}
                  <div>
                    <Label className="text-xs mb-1 block">
                      {language === 'es' ? 'Respuesta pública (visible para el remitente)' : 'Public response (visible to sender)'}
                    </Label>
                    <Textarea 
                      value={respuestaPublica}
                      onChange={(e) => setRespuestaPublica(e.target.value)}
                      rows={2}
                      placeholder={language === 'es' ? 'Escribe una respuesta...' : 'Write a response...'}
                      className="text-sm"
                    />
                  </div>

                  {/* Notas internas */}
                  <div>
                    <Label className="text-xs mb-1 block">
                      {language === 'es' ? 'Notas internas (solo admin)' : 'Internal notes (admin only)'}
                    </Label>
                    <Textarea 
                      value={notasInternas}
                      onChange={(e) => setNotasInternas(e.target.value)}
                      rows={2}
                      placeholder={language === 'es' ? 'Notas privadas...' : 'Private notes...'}
                      className="text-sm bg-yellow-50"
                    />
                  </div>

                  {/* Acciones */}
                  <div className="flex flex-wrap gap-2 pt-2 border-t">
                    {selectedMensaje.estado === 'NUEVO' && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleUpdateMensaje('EN_REVISION')}
                        disabled={updating}
                      >
                        {language === 'es' ? 'Marcar en revisión' : 'Mark in review'}
                      </Button>
                    )}
                    {selectedMensaje.estado !== 'ATENDIDO' && (
                      <Button 
                        size="sm" 
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleUpdateMensaje('ATENDIDO')}
                        disabled={updating}
                      >
                        {language === 'es' ? 'Marcar atendido' : 'Mark attended'}
                      </Button>
                    )}
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleUpdateMensaje(selectedMensaje.estado)}
                      disabled={updating}
                    >
                      {updating && <RefreshCw className="w-3 h-3 mr-1 animate-spin" />}
                      {language === 'es' ? 'Guardar cambios' : 'Save changes'}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="border-purple-300 text-purple-700"
                      onClick={() => setShowCrearAccion(true)}
                    >
                      <Zap className="w-3 h-3 mr-1" />
                      {language === 'es' ? 'Generar acción' : 'Generate action'}
                    </Button>
                  </div>

                  {/* Dialog para crear acción */}
                  {showCrearAccion && (
                    <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 mt-2">
                      <Label className="text-xs mb-1 block">
                        {language === 'es' ? 'Título de la acción' : 'Action title'}
                      </Label>
                      <div className="flex gap-2">
                        <Input 
                          value={tituloAccion}
                          onChange={(e) => setTituloAccion(e.target.value)}
                          placeholder={language === 'es' ? 'Ej: Investigar reporte de acoso' : 'E.g.: Investigate harassment report'}
                          className="text-sm"
                        />
                        <Button size="sm" onClick={handleCrearAccion} disabled={!tituloAccion.trim() || updating}>
                          {language === 'es' ? 'Crear' : 'Create'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setShowCrearAccion(false)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-400">
                  <Mail className="w-12 h-12 mb-2 opacity-50" />
                  <p className="text-sm">{language === 'es' ? 'Selecciona un mensaje' : 'Select a message'}</p>
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {language === 'es' ? 'Cerrar' : 'Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NOM035Panel;
